import React, { useEffect, useRef, useState } from "react";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircleOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  RightOutlined,
  SendOutlined,
  StopOutlined,
  UploadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Breadcrumb,
  Button,
  Card,
  Collapse,
  Divider,
  Dropdown,
  Empty,
  Input,
  Layout,
  Menu,
  Progress,
  Select,
  Skeleton,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useNavigate } from "raviger";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AIPromptTestingModal from "@/Components/Common/AIPromptTestingModal";
import AppLayout from "@/Components/Common/AppLayout";
import { useAuth } from "@/Context/AuthContext";
import { LoadingContext } from "@/Context/LoadingContext";
import { sortResumeForDisplay } from "@/Utils/DateSortingUtils";

const { Title, Text } = Typography;
const { Content } = Layout;
const { TextArea } = Input;
const { Panel } = Collapse;

// Helper function to get score color
const getScoreColor = (score) => {
  if (score >= 80) return "#52c41a"; // Green
  if (score >= 60) return "#faad14"; // Orange
  return "#ff4d4f"; // Red
};

// Helper function to calculate section score from AI analysis
const calculateSectionScore = (aiAnalysis) => {
  if (!aiAnalysis) return null;

  // If score is directly provided, use it
  if (aiAnalysis.score !== undefined) return aiAnalysis.score;

  // Intelligent scoring based on AI analysis quality
  let score = 60; // Lower base score for more realistic assessment

  // Analyze whatsGood content (0-25 points)
  if (aiAnalysis.whatsGood) {
    const goodPoints = aiAnalysis.whatsGood.split(/[\u2022\n,]/).filter((point) => point.trim().length > 10);
    score += Math.min(goodPoints.length * 8, 25); // Up to 25 points for good aspects
  }

  // Analyze whatToImprove content (impact on score)
  if (aiAnalysis.whatToImprove) {
    const improvementPoints = aiAnalysis.whatToImprove
      .split(/[\u2022\n,]/)
      .filter((point) => point.trim().length > 10);
    const penalty = Math.min(improvementPoints.length * 3, 15); // Reduce score based on issues found
    score -= penalty;
  }

  // Analyze aiSuggestions content (0-15 points for actionability)
  if (aiAnalysis.aiSuggestions) {
    const suggestionPoints = aiAnalysis.aiSuggestions
      .split(/[\u2022\n,]/)
      .filter((point) => point.trim().length > 10);
    score += Math.min(suggestionPoints.length * 5, 15); // Up to 15 points for suggestions
  }

  // Ensure score is within realistic bounds
  return Math.min(Math.max(score, 40), 100); // Min 40%, Max 100%
};

// Helper function to format AI analysis text with proper line breaks
const formatAnalysisText = (text) => {
  if (!text) return null;

  // Split by bullet points, newlines, or numbered points
  const points = text.split(/[\u2022\n]|\d+\./).filter((point) => point.trim().length > 0);

  // If we have multiple points, format them as separate lines
  if (points.length > 1) {
    return points.map((point, index) => (
      <div key={index} style={{ marginBottom: "2px" }}>
        • {point.trim()}
      </div>
    ));
  }

  // If single point or no bullet structure, return as is
  return <div>{text}</div>;
};

// Generic AI Analysis Tooltip Component
const AIAnalysisTooltip = ({ analysisData, sectionName, itemIndex }) => {
  if (!analysisData) {
    return (
      <div style={{ padding: "8px" }}>
        <div style={{ fontSize: "13px", color: "#666" }}>
          💡 Run <strong>@enhance-{sectionName}</strong> command to get AI analysis
        </div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return "#52c41a"; // Green
    if (score >= 60) return "#faad14"; // Orange
    return "#ff4d4f"; // Red
  };

  const tooltipContent = (
    <div style={{ maxWidth: "400px", padding: "8px", fontSize: "13px" }}>
      {/* Section Header */}
      <div
        style={{
          fontSize: "14px",
          fontWeight: "600",
          marginBottom: "8px",
          color: "#1677ff",
          textAlign: "center",
        }}
      >
        🤖 AI Analysis {itemIndex !== undefined ? `- Item ${itemIndex + 1}` : ""}
      </div>

      {/* Score Section */}
      {analysisData.score !== undefined && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "8px",
            padding: "6px 12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            border: "1px solid #e6e8eb",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: getScoreColor(analysisData.score),
              textAlign: "center",
            }}
          >
            Score : {analysisData.score}
          </div>
        </div>
      )}

      {/* What's Good Section */}
      {analysisData.whatsGood && (
        <div style={{ marginBottom: "8px" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "600",
              marginBottom: "4px",
              color: "#52c41a",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "4px" }}>✅</span>
            What's Good:
          </div>
          <div
            style={{
              fontSize: "12px",
              lineHeight: "1.4",
              padding: "6px 8px",
              backgroundColor: "#f6ffed",
              borderRadius: "4px",
              border: "1px solid #b7eb8f",
            }}
          >
            {formatAnalysisText(analysisData.whatsGood)}
          </div>
        </div>
      )}

      {/* What to Improve Section */}
      {analysisData.whatToImprove && (
        <div style={{ marginBottom: "8px" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "600",
              marginBottom: "4px",
              color: "#ff4d4f",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "4px" }}>⚠️</span>
            What to Improve:
          </div>
          <div
            style={{
              fontSize: "12px",
              lineHeight: "1.4",
              padding: "6px 8px",
              backgroundColor: "#fff2f0",
              borderRadius: "4px",
              border: "1px solid #ffccc7",
            }}
          >
            {formatAnalysisText(analysisData.whatToImprove)}
          </div>
        </div>
      )}

      {/* AI Suggestions Section */}
      {analysisData.aiSuggestions && (
        <div style={{ marginBottom: "8px" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "600",
              marginBottom: "4px",
              color: "#1677ff",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "4px" }}>💡</span>
            AI Suggestions:
          </div>
          <div
            style={{
              fontSize: "12px",
              lineHeight: "1.4",
              padding: "6px 8px",
              backgroundColor: "#f0f7ff",
              borderRadius: "4px",
              border: "1px solid #d6e4ff",
            }}
          >
            {formatAnalysisText(analysisData.aiSuggestions)}
          </div>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          fontSize: "10px",
          color: "#999",
          textAlign: "center",
          marginTop: "8px",
          paddingTop: "4px",
          borderTop: "1px solid #f5f5f5",
        }}
      >
        AI Analysis • Use commands to enhance this section
      </div>
    </div>
  );

  return tooltipContent;
};

// Legacy Summary Analysis Tooltip Component (for backward compatibility)
const SummaryAnalysisTooltip = ({ summaryAnalysis }) => {
  if (!summaryAnalysis) {
    return <div style={{ padding: "8px" }}>No analysis data available</div>;
  }

  const getScoreColor = (score) => {
    if (score >= 80) return "#52c41a"; // Green
    if (score >= 60) return "#faad14"; // Orange
    return "#ff4d4f"; // Red
  };

  const tooltipContent = (
    <div style={{ maxWidth: "400px", padding: "6px", fontSize: "14px" }}>
      {/* Score Section - Compact Header */}
      {summaryAnalysis.score !== undefined && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "6px",
            padding: "3px 6px",
            backgroundColor: "#f8f9fa",
            borderRadius: "3px",
            borderLeft: `3px solid ${getScoreColor(summaryAnalysis.score)}`,
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: getScoreColor(summaryAnalysis.score),
              textAlign: "center",
            }}
          >
            Score : {summaryAnalysis.score}
          </div>
        </div>
      )}

      {/* Two-Column Layout for Sections */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px",
          marginBottom: "4px",
        }}
      >
        {/* Left Column - Strengths */}
        {summaryAnalysis.whats_good && (
          <div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#52c41a",
                marginBottom: "2px",
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: "1px",
              }}
            >
              ✅ Strengths
            </div>
            <div style={{ fontSize: "12px", color: "#333", lineHeight: "1.2" }}>
              {Array.isArray(summaryAnalysis.whats_good)
                ? summaryAnalysis.whats_good.map((item, index) => (
                    <div key={index} style={{ marginBottom: "1px" }}>
                      ➣ {item}
                    </div>
                  ))
                : summaryAnalysis.whats_good}
            </div>
          </div>
        )}

        {/* Right Column - Improvements */}
        {summaryAnalysis.what_can_be_a_problem && (
          <div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#faad14",
                marginBottom: "2px",
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: "1px",
              }}
            >
              ⚠️ Improvements
            </div>
            <div style={{ fontSize: "12px", color: "#333", lineHeight: "1.2" }}>
              {Array.isArray(summaryAnalysis.what_can_be_a_problem)
                ? summaryAnalysis.what_can_be_a_problem.map((item, index) => (
                    <div key={index} style={{ marginBottom: "1px" }}>
                      ➣ {item}
                    </div>
                  ))
                : summaryAnalysis.what_can_be_a_problem}
            </div>
          </div>
        )}
      </div>

      {/* Full Width - Suggestions */}
      {summaryAnalysis.improvement_feedback && (
        <div style={{ marginTop: "4px" }}>
          <div
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "#1890ff",
              marginBottom: "2px",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: "1px",
            }}
          >
            💡 AI Suggestions
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#333",
              lineHeight: "1.2",
              columnCount: 2,
              columnGap: "8px",
            }}
          >
            {Array.isArray(summaryAnalysis.improvement_feedback)
              ? summaryAnalysis.improvement_feedback.map((item, index) => {
                  const suggestion = typeof item === "object" ? item.suggested_solutions : item;
                  return (
                    <div
                      key={index}
                      style={{
                        marginBottom: "1px",
                        breakInside: "avoid",
                      }}
                    >
                      ➣ {suggestion}
                    </div>
                  );
                })
              : summaryAnalysis.improvement_feedback}
          </div>
        </div>
      )}

      {/* Minimal Footer */}
      <div
        style={{
          fontSize: "11px",
          color: "#999",
          textAlign: "center",
          marginTop: "4px",
          paddingTop: "2px",
          borderTop: "1px solid #f5f5f5",
        }}
      >
        AI Analysis • Run commands for more details
      </div>
    </div>
  );

  return tooltipContent;
};

// Resume Status Panel Component
const ResumeStatusPanel = ({ resumeData, onPreview }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const calculateCompletionPercentage = () => {
    if (!resumeData?.parsedData) return 0;

    // Dynamically get all sections from parsed data
    const allSections = Object.keys(resumeData.parsedData).filter((key) => key !== "rawContent");

    const completedSections = allSections.filter((section) => {
      const data = resumeData.parsedData[section];
      if (!data) return false;
      if (Array.isArray(data)) return data.length > 0;
      if (typeof data === "object") return Object.keys(data).some((key) => data[key]);
      return Boolean(data);
    });

    return Math.round((completedSections.length / allSections.length) * 100);
  };

  const getSectionStatus = (section, data) => {
    if (!data) return { status: "missing", icon: ExclamationCircleOutlined, color: "error" };

    // Handle arrays
    if (Array.isArray(data)) {
      if (data.length === 0) return { status: "missing", icon: ExclamationCircleOutlined, color: "error" };
      if (data.length < 2) return { status: "warning", icon: WarningOutlined, color: "warning" };
      return { status: "complete", icon: CheckCircleOutlined, color: "success" };
    }

    // Handle objects
    if (typeof data === "object" && data !== null) {
      const hasData = Object.values(data).some((value) => Boolean(value));
      if (!hasData) return { status: "missing", icon: ExclamationCircleOutlined, color: "error" };
      return { status: "complete", icon: CheckCircleOutlined, color: "success" };
    }

    // Handle strings and other primitives
    if (typeof data === "string") {
      if (data.trim().length === 0) return { status: "missing", icon: ExclamationCircleOutlined, color: "error" };
      if (data.trim().length < 50) return { status: "warning", icon: WarningOutlined, color: "warning" };
      return { status: "complete", icon: CheckCircleOutlined, color: "success" };
    }

    // Handle other data types
    return Boolean(data)
      ? { status: "complete", icon: CheckCircleOutlined, color: "success" }
      : { status: "missing", icon: ExclamationCircleOutlined, color: "error" };
  };

  // Helper function to get display name for sections
  const getSectionLabel = (key, data) => {
    const labelMap = {
      personalInfo: "Personal Info",
      summary: "Professional Summary",
      experience: `Work Experience (${Array.isArray(data) ? data.length : 0} jobs)`,
      education: `Education (${Array.isArray(data) ? data.length : 0} entries)`,
      skills: `Skills (${Array.isArray(data) ? data.length : data?.list ? data.list.length : 0})`,
      projects: `Projects (${Array.isArray(data) ? data.length : 0})`,
      certifications: `Certifications (${Array.isArray(data) ? data.length : 0})`,
      languages: `Languages (${Array.isArray(data) ? data.length : 0})`,
    };
    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Sort the data before generating sections
  const sortedData = resumeData?.parsedData ? sortResumeForDisplay(resumeData.parsedData) : null;

  // Dynamically generate sections from parsed data
  const sections = sortedData
    ? Object.keys(sortedData)
        .filter((key) => key !== "rawContent") // Exclude internal fields
        .map((key) => ({
          key,
          label: getSectionLabel(key, sortedData[key]),
          data: sortedData[key],
          details: sortedData[key],
        }))
        .sort((a, b) => {
          // Sort sections in logical order
          const order = [
            "personalInfo",
            "summary",
            "experience",
            "education",
            "skills",
            "projects",
            "certifications",
            "languages",
          ];
          const aIndex = order.indexOf(a.key);
          const bIndex = order.indexOf(b.key);
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.key.localeCompare(b.key);
        })
    : [];

  // Generate quick issues dynamically
  const quickIssues = [];
  if (resumeData?.parsedData) {
    // Check for missing summary
    const summaryText =
      typeof resumeData.parsedData.summary === "string"
        ? resumeData.parsedData.summary
        : resumeData.parsedData.summary?.content || "";
    if (!resumeData.parsedData.summary || summaryText.trim().length < 20) {
      quickIssues.push("Add or enhance professional summary");
    }

    // Check education dates
    if (resumeData.parsedData.education && Array.isArray(resumeData.parsedData.education)) {
      if (!resumeData.parsedData.education.some((edu) => edu.endDate)) {
        quickIssues.push("Missing graduation dates");
      }
    }

    // Check skills count
    const skillsList = Array.isArray(resumeData.parsedData.skills)
      ? resumeData.parsedData.skills
      : resumeData.parsedData.skills?.list || [];
    if (!resumeData.parsedData.skills || skillsList.length < 5) {
      quickIssues.push("Add more skills (minimum 5 recommended)");
    }

    // Check for missing contact information
    if (!resumeData.parsedData.personalInfo?.email) {
      quickIssues.push("Missing email address");
    }
    if (!resumeData.parsedData.personalInfo?.phone) {
      quickIssues.push("Missing phone number");
    }

    // Check for work experience
    if (!resumeData.parsedData.experience || resumeData.parsedData.experience.length === 0) {
      quickIssues.push("No work experience listed");
    }

    // Check for missing LinkedIn or professional profiles
    if (!resumeData.parsedData.personalInfo?.linkedIn && !resumeData.parsedData.personalInfo?.github) {
      quickIssues.push("Consider adding LinkedIn or GitHub profile");
    }
  }

  const completionPercentage = calculateCompletionPercentage();

  const renderSectionData = (sectionKey, data) => {
    if (!data) return <Text type="secondary">No data</Text>;

    switch (sectionKey) {
      case "personalInfo":
        return (
          <Space direction="vertical" size="small">
            {data.name && (
              <Text>
                <strong>Name:</strong> {data.name}
              </Text>
            )}
            {data.email && (
              <Text>
                <strong>Email:</strong> {data.email}
              </Text>
            )}
            {data.phone && (
              <Text>
                <strong>Phone:</strong> {data.phone}
              </Text>
            )}
            {data.location && (
              <Text>
                <strong>Location:</strong> {data.location}
              </Text>
            )}
            {data.linkedIn && (
              <Text>
                <strong>LinkedIn:</strong> {data.linkedIn}
              </Text>
            )}
            {data.github && (
              <Text>
                <strong>GitHub:</strong> {data.github}
              </Text>
            )}
          </Space>
        );

      case "experience":
        return (
          <Space direction="vertical" size="middle">
            {Array.isArray(data) ? (
              data.map((exp, index) => (
                <Card key={index} size="small">
                  <Space direction="vertical" size="small">
                    <Text strong>{exp.title}</Text>
                    <Text>
                      {exp.company}
                      {exp.location && ` • ${exp.location}`}
                    </Text>
                    <Text type="secondary">
                      {exp.startDate} - {exp.endDate || "Present"}
                    </Text>
                    {exp.description && (
                      <Text>
                        <span style={{ whiteSpace: "pre-line" }}>{exp.description.substring(0, 100)}...</span>
                      </Text>
                    )}
                  </Space>
                </Card>
              ))
            ) : (
              <Text>Invalid experience data</Text>
            )}
          </Space>
        );

      case "education":
        return (
          <Space direction="vertical" size="middle">
            {Array.isArray(data) ? (
              data.map((edu, index) => (
                <Card key={index} size="small">
                  <Space direction="vertical" size="small">
                    <Text strong>{edu.degree}</Text>
                    <Text>
                      {edu.school}
                      {edu.location && ` • ${edu.location}`}
                    </Text>
                    <Text type="secondary">
                      {edu.startDate} - {edu.endDate || "Present"}
                    </Text>
                    {edu.gpa && <Text>GPA: {edu.gpa}</Text>}
                  </Space>
                </Card>
              ))
            ) : (
              <Text>Invalid education data</Text>
            )}
          </Space>
        );

      case "skills":
        return (
          <Space wrap>
            {Array.isArray(data) ? (
              data.map((skill, index) => <Tag key={index}>{skill}</Tag>)
            ) : (
              <Text>Invalid skills data</Text>
            )}
          </Space>
        );

      case "projects":
        return (
          <Space direction="vertical" size="middle">
            {Array.isArray(data) ? (
              data.map((project, index) => (
                <Card key={index} size="small">
                  <Space direction="vertical" size="small">
                    <Text strong>{project.name}</Text>
                    {project.description && (
                      <Text>
                        <span style={{ whiteSpace: "pre-line" }}>{project.description.substring(0, 100)}...</span>
                      </Text>
                    )}
                    {project.technologies && (
                      <Space wrap>
                        {project.technologies.map((tech, techIndex) => (
                          <Tag key={techIndex} size="small">
                            {tech}
                          </Tag>
                        ))}
                      </Space>
                    )}
                  </Space>
                </Card>
              ))
            ) : (
              <Text>Invalid projects data</Text>
            )}
          </Space>
        );

      case "certifications":
        return (
          <Space direction="vertical" size="middle">
            {Array.isArray(data) && data.length > 0 ? (
              data.map((cert, index) => (
                <Card key={index} size="small">
                  <Space direction="vertical" size="small">
                    <Text strong>{cert.name}</Text>
                    <Text>{cert.issuer}</Text>
                    <Text type="secondary">{cert.date}</Text>
                  </Space>
                </Card>
              ))
            ) : (
              <Text type="secondary">No certifications</Text>
            )}
          </Space>
        );

      case "summary":
        return (
          <Space direction="vertical" size="small">
            <Text>{typeof data === "string" ? data : JSON.stringify(data)}</Text>
          </Space>
        );

      case "languages":
        return (
          <Space direction="vertical" size="middle">
            {Array.isArray(data) && data.length > 0 ? (
              data.map((lang, index) => (
                <Card key={index} size="small">
                  <Space direction="vertical" size="small">
                    <Text strong>{lang.language}</Text>
                    <Text type="secondary">{lang.proficiency}</Text>
                  </Space>
                </Card>
              ))
            ) : (
              <Text type="secondary">No languages</Text>
            )}
          </Space>
        );

      default:
        // Generic handler for any other fields
        if (typeof data === "string") {
          return <Text>{data}</Text>;
        } else if (Array.isArray(data)) {
          return (
            <Space direction="vertical" size="small">
              {data.map((item, index) => (
                <Text key={index}>{typeof item === "object" ? JSON.stringify(item, null, 2) : String(item)}</Text>
              ))}
            </Space>
          );
        } else if (typeof data === "object" && data !== null) {
          return (
            <Space direction="vertical" size="small">
              {Object.entries(data).map(
                ([key, value]) =>
                  value && (
                    <Text key={key}>
                      <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {String(value)}
                    </Text>
                  )
              )}
            </Space>
          );
        } else {
          return <Text>{String(data || "No data")}</Text>;
        }
    }
  };

  const collapseItems = sections.map((section) => {
    const { status, icon: StatusIcon, color } = getSectionStatus(section.key, section.data);
    const IconComponent = StatusIcon;

    return {
      key: section.key,
      label: (
        <Space>
          <IconComponent
            style={{ color: color === "error" ? "#ff4d4f" : color === "warning" ? "#faad14" : "#52c41a" }}
          />
          <Text>{section.label}</Text>
        </Space>
      ),
      children: (
        <div style={{ marginLeft: "24px" }}>
          <Space direction="vertical" size="small">
            {section.details && renderSectionData(section.key, section.details)}
          </Space>
        </div>
      ),
    };
  });

  return (
    <div style={{ height: "calc(100vh - 120px)", overflowY: "auto" }}>
      <Card>
        <div style={{ padding: "12px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Title level={4} style={{ margin: 0 }}>
              📋 Resume Status
            </Title>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Title level={5} style={{ color: "#1890ff", margin: 0 }}>
                Completion: {completionPercentage}%
              </Title>
              <Progress percent={completionPercentage} strokeColor="#52c41a" size="small" />
            </div>

            <Divider style={{ margin: "8px 0" }} />

            <Collapse items={collapseItems} ghost size="small" />

            {quickIssues.length > 0 && (
              <>
                <Divider style={{ margin: "8px 0" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Title level={5} style={{ margin: 0 }}>
                    🔍 Quick Issues:
                  </Title>
                  {quickIssues.map((issue, index) => (
                    <Text key={index} type="secondary" style={{ fontSize: "12px" }}>
                      • {issue}
                    </Text>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Preview Button */}
          <div style={{ marginTop: "16px" }}>
            <Button block type="primary" icon={<PlusOutlined />} onClick={onPreview} size="small">
              Download Resume
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Resume Preview Panel Component
const ResumePreviewPanel = ({ resumeData, onPreview, summaryAnalysis, isAdminMode, handleAITest }) => {
  if (!resumeData?.parsedData) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Title level={4} style={{ margin: 0, marginBottom: "16px" }}>
              📄 Resume Preview
            </Title>
            <Empty
              description="Loading resume preview..."
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            />
          </div>
        </Card>
      </div>
    );
  }

  const data = sortResumeForDisplay(resumeData.parsedData);

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ffffff",
          border: "none",
        }}
      >
        {/* Header with Preview Resume button */}
        <div
          style={{
            padding: "12px 16px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="primary"
            onClick={onPreview}
            size="small"
            style={{
              borderRadius: "6px",
              fontSize: "12px",
              height: "28px",
            }}
          >
            Download Resume
          </Button>
        </div>

        {/* Scrollable Resume Content */}
        <div
          style={{
            flex: 1,
            overflowY: "scroll",
            overflowX: "hidden",
            padding: "8px 16px 16px 16px",
            height: "calc(100vh - 220px)",
            scrollbarWidth: "thin",
            scrollbarColor: "#d1d5db #f9fafb",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Classic Resume Template Preview */}
          <div
            className="resume-preview-container"
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e1e4e8",
              borderRadius: "12px",
              padding: "32px 28px",
              fontFamily: "Arial, sans-serif",
              fontSize: "14px",
              lineHeight: "1.4",
              marginBottom: "16px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.12)",
              transition: "box-shadow 0.2s ease",
              position: "relative",
              maxWidth: "100%",
              margin: "0 auto",
              minHeight: "800px",
            }}
          >
            {/* Header - Personal Info */}
            {data.personalInfo && (
              <Tooltip
                title={
                  <AIAnalysisTooltip analysisData={data.personalInfo.aiAnalysis} sectionName="personalInfo" />
                }
                placement="left"
                trigger="hover"
                overlayStyle={{ maxWidth: "400px" }}
                color="white"
                overlayInnerStyle={{ color: "#333" }}
              >
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "20px",
                    borderBottom: "2px solid #333",
                    paddingBottom: "12px",
                    cursor: data.personalInfo.aiAnalysis ? "pointer" : "default",
                  }}
                >
                  <Title level={2} style={{ margin: 0, fontSize: "20px", fontWeight: "bold", color: "#333" }}>
                    {data.personalInfo.name || ""}
                    {data.personalInfo.aiAnalysis && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "10px",
                          fontWeight: "normal",
                          backgroundColor: getScoreColor(calculateSectionScore(data.personalInfo.aiAnalysis)),
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "10px",
                        }}
                      >
                        {calculateSectionScore(data.personalInfo.aiAnalysis)}
                      </span>
                    )}
                  </Title>
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                    {data.personalInfo.email && <span>{data.personalInfo.email}</span>}
                    {data.personalInfo.phone && data.personalInfo.email && <span> • </span>}
                    {data.personalInfo.phone && <span>{data.personalInfo.phone}</span>}
                    {(data.personalInfo.email || data.personalInfo.phone) && data.personalInfo.location && (
                      <span> • </span>
                    )}
                    {data.personalInfo.location && <span>{data.personalInfo.location}</span>}
                  </div>
                  {(data.personalInfo.linkedIn || data.personalInfo.github) && (
                    <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>
                      {data.personalInfo.linkedIn && <span>LinkedIn: {data.personalInfo.linkedIn}</span>}
                      {data.personalInfo.linkedIn && data.personalInfo.github && <span> • </span>}
                      {data.personalInfo.github && <span>GitHub: {data.personalInfo.github}</span>}
                    </div>
                  )}
                </div>
              </Tooltip>
            )}

            {/* Professional Summary */}
            {((typeof data.summary === "string" && data.summary.trim()) ||
              (typeof data.summary === "object" && data.summary?.content && data.summary.content.trim())) && (
              <Tooltip
                title={
                  data.summary?.aiAnalysis ? (
                    <AIAnalysisTooltip analysisData={data.summary.aiAnalysis} sectionName="summary" />
                  ) : (
                    <div style={{ padding: "8px" }}>
                      <div style={{ fontSize: "13px", color: "#666" }}>
                        💡 Run <strong>@enhance-summary</strong> or <strong>@summary</strong> command to get AI
                        analysis
                      </div>
                    </div>
                  )
                }
                placement="left"
                trigger="hover"
                overlayStyle={{ maxWidth: "400px" }}
                color="white"
                overlayInnerStyle={{ color: "#333" }}
                onVisibleChange={(visible) => {
                  if (visible) {
                  }
                }}
              >
                <div style={{ marginBottom: "16px", cursor: data.summary?.aiAnalysis ? "pointer" : "default" }}>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "15px",
                      fontWeight: "bold",
                      color: "#333",
                      marginBottom: "8px",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "3px",
                    }}
                  >
                    PROFESSIONAL SUMMARY
                    {data.summary?.aiAnalysis && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "10px",
                          fontWeight: "normal",
                          backgroundColor: getScoreColor(calculateSectionScore(data.summary.aiAnalysis)),
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "10px",
                        }}
                      >
                        {calculateSectionScore(data.summary.aiAnalysis)}
                      </span>
                    )}
                    {isAdminMode && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<ExclamationCircleOutlined />}
                        onClick={() => handleAITest("summary")}
                        style={{
                          marginLeft: "8px",
                          fontSize: "10px",
                          height: "20px",
                          borderRadius: "10px",
                          backgroundColor: "#52c41a",
                          borderColor: "#52c41a",
                        }}
                      >
                        AI Test
                      </Button>
                    )}
                  </h3>
                  <p style={{ margin: 0, fontSize: "13px", lineHeight: "1.4", textAlign: "justify" }}>
                    {typeof data.summary === "string" ? data.summary : data.summary?.content || data.summary}
                  </p>
                </div>
              </Tooltip>
            )}

            {/* Experience */}
            {data.experience && data.experience.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#333",
                    marginBottom: "8px",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "3px",
                  }}
                >
                  WORK EXPERIENCE
                  {isAdminMode && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<ExclamationCircleOutlined />}
                      onClick={() => handleAITest("experience")}
                      style={{
                        marginLeft: "8px",
                        fontSize: "10px",
                        height: "20px",
                        borderRadius: "10px",
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                      }}
                    >
                      AI Test
                    </Button>
                  )}
                </h3>
                {data.experience.map((exp, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <AIAnalysisTooltip
                        analysisData={exp.aiAnalysis}
                        sectionName="experience"
                        itemIndex={index}
                      />
                    }
                    placement="left"
                    trigger="hover"
                    overlayStyle={{ maxWidth: "400px" }}
                    color="white"
                    overlayInnerStyle={{ color: "#333" }}
                  >
                    <div
                      style={{
                        marginBottom: "14px",
                        cursor: exp.aiAnalysis ? "pointer" : "default",
                        padding: "8px",
                        borderRadius: "4px",
                        border: exp.aiAnalysis ? "1px solid #f0f0f0" : "none",
                        backgroundColor: exp.aiAnalysis ? "#fafafa" : "transparent",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          marginBottom: "3px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {exp.title && <strong style={{ fontSize: "13px", color: "#333" }}>{exp.title}</strong>}
                          {exp.aiAnalysis && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "9px",
                                fontWeight: "normal",
                                backgroundColor: getScoreColor(calculateSectionScore(exp.aiAnalysis)),
                                color: "white",
                                padding: "1px 4px",
                                borderRadius: "8px",
                              }}
                            >
                              {calculateSectionScore(exp.aiAnalysis)}
                            </span>
                          )}
                        </div>
                        {(exp.startDate || exp.endDate) && (
                          <span style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {exp.startDate || ""} - {exp.endDate || "Present"}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>
                        <em>
                          {exp.company || ""}
                          {exp.location && `, ${exp.location}`}
                        </em>
                      </div>
                      {exp.description && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            lineHeight: "1.3",
                            textAlign: "justify",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {exp.description}
                        </p>
                      )}
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Education */}
            {data.education && data.education.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#333",
                    marginBottom: "8px",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "3px",
                  }}
                >
                  EDUCATION
                  {isAdminMode && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<ExclamationCircleOutlined />}
                      onClick={() => handleAITest("education")}
                      style={{
                        marginLeft: "8px",
                        fontSize: "10px",
                        height: "20px",
                        borderRadius: "10px",
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                      }}
                    >
                      AI Test
                    </Button>
                  )}
                </h3>
                {data.education.map((edu, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <AIAnalysisTooltip analysisData={edu.aiAnalysis} sectionName="education" itemIndex={index} />
                    }
                    placement="left"
                    trigger="hover"
                    overlayStyle={{ maxWidth: "400px" }}
                    color="white"
                    overlayInnerStyle={{ color: "#333" }}
                  >
                    <div
                      style={{
                        marginBottom: "10px",
                        cursor: edu.aiAnalysis ? "pointer" : "default",
                        padding: "8px",
                        borderRadius: "4px",
                        border: edu.aiAnalysis ? "1px solid #f0f0f0" : "none",
                        backgroundColor: edu.aiAnalysis ? "#fafafa" : "transparent",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          marginBottom: "3px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {edu.degree && <strong style={{ fontSize: "13px", color: "#333" }}>{edu.degree}</strong>}
                          {edu.aiAnalysis && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "9px",
                                fontWeight: "normal",
                                backgroundColor: getScoreColor(calculateSectionScore(edu.aiAnalysis)),
                                color: "white",
                                padding: "1px 4px",
                                borderRadius: "8px",
                              }}
                            >
                              {calculateSectionScore(edu.aiAnalysis)}
                            </span>
                          )}
                        </div>
                        {(edu.startDate || edu.endDate) && (
                          <span style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {edu.startDate || ""} - {edu.endDate || ""}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "12px", color: "#555" }}>
                        <em>
                          {edu.school || ""}
                          {edu.location && `, ${edu.location}`}
                        </em>
                        {edu.gpa && <span style={{ marginLeft: "8px" }}>GPA: {edu.gpa}</span>}
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Skills */}
            {((Array.isArray(data.skills) && data.skills.length > 0) ||
              (data.skills?.list && data.skills.list.length > 0)) && (
              <Tooltip
                title={<AIAnalysisTooltip analysisData={data.skills?.aiAnalysis} sectionName="skills" />}
                placement="left"
                trigger="hover"
                overlayStyle={{ maxWidth: "400px" }}
                color="white"
                overlayInnerStyle={{ color: "#333" }}
              >
                <div
                  style={{
                    marginBottom: "16px",
                    cursor: data.skills?.aiAnalysis ? "pointer" : "default",
                    padding: "8px",
                    borderRadius: "4px",
                    border: data.skills?.aiAnalysis ? "1px solid #f0f0f0" : "none",
                    backgroundColor: data.skills?.aiAnalysis ? "#fafafa" : "transparent",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "15px",
                      fontWeight: "bold",
                      color: "#333",
                      marginBottom: "8px",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "3px",
                    }}
                  >
                    SKILLS
                    {data.skills?.aiAnalysis && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "10px",
                          fontWeight: "normal",
                          backgroundColor: getScoreColor(calculateSectionScore(data.skills.aiAnalysis)),
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "10px",
                        }}
                      >
                        {calculateSectionScore(data.skills.aiAnalysis)}
                      </span>
                    )}
                    {isAdminMode && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<ExclamationCircleOutlined />}
                        onClick={() => handleAITest("skills")}
                        style={{
                          marginLeft: "8px",
                          fontSize: "10px",
                          height: "20px",
                          borderRadius: "10px",
                          backgroundColor: "#52c41a",
                          borderColor: "#52c41a",
                        }}
                      >
                        AI Test
                      </Button>
                    )}
                  </h3>
                  <div
                    style={{
                      fontSize: "13px",
                      lineHeight: "1.1",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "2px 8px",
                      columnGap: "8px",
                    }}
                  >
                    {(Array.isArray(data.skills) ? data.skills : data.skills?.list || []).map((skill, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          wordWrap: "break-word",
                          marginBottom: "2px",
                          lineHeight: "1.2",
                        }}
                      >
                        <span style={{ marginRight: "4px", flexShrink: "0" }}>•</span>
                        <span>{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Tooltip>
            )}

            {/* Projects */}
            {data.projects && data.projects.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#333",
                    marginBottom: "8px",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "3px",
                  }}
                >
                  PROJECTS
                  {isAdminMode && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<ExclamationCircleOutlined />}
                      onClick={() => handleAITest("projects")}
                      style={{
                        marginLeft: "8px",
                        fontSize: "10px",
                        height: "20px",
                        borderRadius: "10px",
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                      }}
                    >
                      AI Test
                    </Button>
                  )}
                </h3>
                {data.projects.map((project, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <AIAnalysisTooltip
                        analysisData={project.aiAnalysis}
                        sectionName="projects"
                        itemIndex={index}
                      />
                    }
                    placement="left"
                    trigger="hover"
                    overlayStyle={{ maxWidth: "400px" }}
                    color="white"
                    overlayInnerStyle={{ color: "#333" }}
                  >
                    <div
                      style={{
                        marginBottom: "12px",
                        cursor: project.aiAnalysis ? "pointer" : "default",
                        padding: "8px",
                        borderRadius: "4px",
                        border: project.aiAnalysis ? "1px solid #f0f0f0" : "none",
                        backgroundColor: project.aiAnalysis ? "#fafafa" : "transparent",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "baseline", marginBottom: "3px" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {(project.name || project.title) && (
                            <strong style={{ fontSize: "13px", color: "#333" }}>
                              {project.name || project.title}
                            </strong>
                          )}
                          {project.aiAnalysis && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "9px",
                                fontWeight: "normal",
                                backgroundColor: getScoreColor(calculateSectionScore(project.aiAnalysis)),
                                color: "white",
                                padding: "1px 4px",
                                borderRadius: "8px",
                              }}
                            >
                              {calculateSectionScore(project.aiAnalysis)}
                            </span>
                          )}
                        </div>
                        {project.link &&
                          project.link !== "null" &&
                          project.link !== null &&
                          project.link.trim() !== "" && (
                            <span style={{ fontSize: "11px", color: "#0366d6", marginLeft: "8px" }}>
                              {project.link}
                            </span>
                          )}
                      </div>
                      {project.description && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            lineHeight: "1.3",
                            marginBottom: "6px",
                            textAlign: "justify",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {project.description}
                        </p>
                      )}
                      {project.technologies && project.technologies.length > 0 && (
                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                          Technologies: {project.technologies.join(", ")}
                        </div>
                      )}
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Certifications */}
            {data.certifications && data.certifications.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#333",
                    marginBottom: "8px",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "3px",
                  }}
                >
                  CERTIFICATIONS
                  {isAdminMode && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<ExclamationCircleOutlined />}
                      onClick={() => handleAITest("certifications")}
                      style={{
                        marginLeft: "8px",
                        fontSize: "10px",
                        height: "20px",
                        borderRadius: "10px",
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                      }}
                    >
                      AI Test
                    </Button>
                  )}
                </h3>
                {data.certifications.map((cert, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <AIAnalysisTooltip
                        analysisData={cert.aiAnalysis}
                        sectionName="certifications"
                        itemIndex={index}
                      />
                    }
                    placement="left"
                    trigger="hover"
                    overlayStyle={{ maxWidth: "400px" }}
                    color="white"
                    overlayInnerStyle={{ color: "#333" }}
                  >
                    <div
                      style={{
                        marginBottom: "8px",
                        cursor: cert.aiAnalysis ? "pointer" : "default",
                        padding: "8px",
                        borderRadius: "4px",
                        border: cert.aiAnalysis ? "1px solid #f0f0f0" : "none",
                        backgroundColor: cert.aiAnalysis ? "#fafafa" : "transparent",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {cert.name && <strong style={{ fontSize: "13px", color: "#333" }}>{cert.name}</strong>}
                          {cert.aiAnalysis && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "9px",
                                fontWeight: "normal",
                                backgroundColor: getScoreColor(calculateSectionScore(cert.aiAnalysis)),
                                color: "white",
                                padding: "1px 4px",
                                borderRadius: "8px",
                              }}
                            >
                              {calculateSectionScore(cert.aiAnalysis)}
                            </span>
                          )}
                        </div>
                        {cert.date && (
                          <span style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>{cert.date}</span>
                        )}
                      </div>
                      {cert.issuer && (
                        <div style={{ fontSize: "12px", color: "#555" }}>
                          <em>{cert.issuer}</em>
                        </div>
                      )}
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Spacing to ensure scrolling */}
            <div style={{ height: "200px" }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Chat Panel Component
const ChatPanel = ({ resumeData, onSendCommand, onPreview }) => {
  const { resumeId } = useParams();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false); // Local loading for chat only
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [selectedSubCommand, setSelectedSubCommand] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedDeleteItems, setSelectedDeleteItems] = useState([]); // For multi-select delete
  const [showDropdown, setShowDropdown] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const fetch = useAuthenticatedFetch();

  // Inline Chat Loading Component
  const ChatLoadingIndicator = () => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        marginBottom: "16px",
        padding: "0 16px",
      }}
    >
      {/* AI Avatar */}
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "4px",
          backgroundColor: "#374151",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: "4px",
        }}
      >
        <span style={{ fontSize: "16px" }}>🤖</span>
      </div>

      {/* Loading Message */}
      <div
        style={{
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "16px",
          padding: "12px 16px",
          maxWidth: "70%",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#6b7280",
            fontSize: "14px",
          }}
        >
          <span>AI is thinking</span>
          <div
            style={{
              display: "flex",
              gap: "2px",
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  backgroundColor: "#6b7280",
                  animation: `pulse 1.4s infinite ease-in-out`,
                  animationDelay: `${i * 0.16}s`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Add CSS animation inline */}
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% {
            opacity: 0.3;
            transform: scale(0.7);
          }
          40% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );

  // Generate dynamic Quick Issues based on resume data
  const generateQuickIssues = (resumeData) => {
    if (!resumeData?.parsedData) {
      return [
        "📋 Upload your resume to get personalized suggestions",
        "🎯 I'll analyze your content for improvements",
        "✨ Let's optimize your professional presentation",
        "🚀 Ready to make your resume stand out?",
      ];
    }

    // Handle blank resumes with contextual suggestions
    if (resumeData?.creationMode === "blank") {
      const { targetRole, experienceLevel, industry } = resumeData?.initialContext || {};
      const blankIssues = [
        `📝 Start by adding your personal information`,
        `💼 Tell me about your ${targetRole} experience`,
        `🎯 Let's create a compelling summary for ${targetRole}`,
        `⚡ Add your technical skills and competencies`,
        `🎓 Include your educational background`,
      ];

      if (experienceLevel === "entry") {
        blankIssues.push(`🌟 Highlight projects, internships, and coursework`);
      } else if (experienceLevel === "senior" || experienceLevel === "executive") {
        blankIssues.push(`🏆 Showcase leadership achievements and major projects`);
      }

      if (industry) {
        blankIssues.push(`🏢 Emphasize ${industry}-specific experience`);
      }

      return blankIssues;
    }

    const data = resumeData.parsedData;
    const issues = [];

    // Check for missing or weak professional summary
    const summaryText = typeof data.summary === "string" ? data.summary : data.summary?.content || "";
    if (!data.summary || summaryText.trim().length < 50) {
      issues.push("📝 Add a compelling professional summary to grab attention");
    }

    // Check work experience quantity and descriptions
    if (!data.experience || data.experience.length < 2) {
      issues.push("💼 Add more work experience to show career progression");
    } else {
      // Check for weak experience descriptions
      const weakDescriptions = data.experience.filter(
        (exp) => !exp.description || exp.description.trim().length < 80
      ).length;
      if (weakDescriptions > 0) {
        issues.push("🎯 Enhance job descriptions with specific achievements");
      }
    }

    // Check skills section
    const skillsList = Array.isArray(data.skills) ? data.skills : data.skills?.list || [];
    if (!data.skills || skillsList.length < 5) {
      issues.push("⚡ Expand skills section with relevant technical abilities");
    }

    // Check for quantifiable achievements
    const hasNumbers = data.experience?.some(
      (exp) => exp.description && /\d+(%|k|million|billion|\$|yr|year|month|week)/.test(exp.description)
    );
    if (!hasNumbers) {
      issues.push("📊 Add quantified results (numbers, percentages, metrics)");
    }

    // Check education details
    if (data.education && data.education.length > 0) {
      const missingGPA = data.education.filter((edu) => !edu.gpa).length;
      if (missingGPA === data.education.length) {
        issues.push("🎓 Consider adding GPA or academic achievements if strong");
      }
    }

    // Check for contact completeness
    if (data.personalInfo) {
      const missing = [];
      if (!data.personalInfo.email) missing.push("email");
      if (!data.personalInfo.phone) missing.push("phone");
      if (!data.personalInfo.location) missing.push("location");
      if (missing.length > 0) {
        issues.push(`📞 Complete contact info (missing: ${missing.join(", ")})`);
      }
    }

    // Check for projects section
    if (!data.projects || data.projects.length === 0) {
      issues.push("🚀 Add relevant projects to demonstrate practical skills");
    }

    // Check for certifications
    if (!data.certifications || data.certifications.length === 0) {
      issues.push("🏆 Include professional certifications or licenses");
    }

    // Check for keywords and action verbs
    const experienceText =
      data.experience
        ?.map((exp) => exp.description)
        .join(" ")
        .toLowerCase() || "";
    const hasActionVerbs =
      /\b(achieved|developed|implemented|managed|created|led|improved|increased|designed|optimized)\b/.test(
        experienceText
      );
    if (!hasActionVerbs) {
      issues.push("💪 Use stronger action verbs to describe achievements");
    }

    // Check resume length (approximate)
    const totalContent = Object.values(data).flat().join(" ").length;
    if (totalContent < 1000) {
      issues.push("📏 Expand content - your resume seems too brief");
    }

    // Return top 4 most relevant issues, filtered to remove any empty strings
    return issues.filter((issue) => issue && issue.trim().length > 0).slice(0, 4);
  };

  // Load messages from database
  const loadChatHistory = async () => {
    try {
      const response = await fetch.get(`/chat/${resumeId}`, false);
      const messages = response.data?.messages || [];

      return messages;
    } catch (error) {
      console.error("Error loading chat history", error.message || error);
      return [];
    }
  };

  // Save message to database
  const saveMessageToChat = async (message) => {
    try {
      await fetch.post("/chat/message", {
        resumeId,
        message,
      });
    } catch (error) {
      console.error("Error saving message", error.message || error);
    }
  };

  // Save entire conversation to database
  const saveChatHistory = async (messagesToSave) => {
    try {
      await fetch.post("/chat/save", {
        resumeId,
        messages: messagesToSave,
      });
    } catch (error) {
      console.error("Error saving chat history", error.message || error);
    }
  };

  // Clear chat history
  const clearChatHistory = async () => {
    try {
      await fetch.delete(`/chat/${resumeId}`);
      const quickIssues = generateQuickIssues(resumeData);
      const initialMessage = [
        {
          id: "welcome-v2-" + Date.now().toString(),
          type: "ai",
          content:
            "Hello! I'm your AI Resume Assistant. I've analyzed your resume and found specific areas that need attention.\n\n**Quick Issues:**",
          suggestions: quickIssues.filter((issue) => issue && issue.trim().length > 0),
          timestamp: new Date().toISOString(),
        },
      ];
      setMessages(initialMessage);
      await saveChatHistory(initialMessage);
    } catch (error) {
      console.error("Error clearing chat history", error.message || error);
    }
  };

  // Dynamic command structure that updates when resume data changes
  const getCommandStructure = () => {
    return {
      "@add": {
        label: "Add New",
        subCommands: {
          personalInfo: { label: "Personal Information", fields: null },
          summary: { label: "Professional Summary", fields: null },
          experience: { label: "Work Experience", fields: null },
          education: { label: "Education", fields: null },
          skills: { label: "Skills", fields: null },
          projects: { label: "Project", fields: null },
          certifications: { label: "Certification", fields: null },
        },
      },
      "@enhance": {
        label: "Enhance or Update",
        subCommands: {
          personalInfo: {
            label: "Personal Information",
            fields:
              resumeData?.parsedData && resumeData.parsedData.personalInfo
                ? (() => {
                    const personalInfo = resumeData.parsedData.personalInfo;
                    const availableFields = [];

                    // Add available personal info fields that have data for enhancement
                    if (personalInfo.name) {
                      availableFields.push({ key: "name", label: `Name: ${personalInfo.name}` });
                    }
                    if (personalInfo.email) {
                      availableFields.push({ key: "email", label: `Email: ${personalInfo.email}` });
                    }
                    if (personalInfo.phone) {
                      availableFields.push({ key: "phone", label: `Phone: ${personalInfo.phone}` });
                    }
                    if (personalInfo.location) {
                      availableFields.push({ key: "location", label: `Location: ${personalInfo.location}` });
                    }
                    if (personalInfo.linkedIn || personalInfo.linkedin) {
                      const linkedinValue = personalInfo.linkedIn || personalInfo.linkedin;
                      availableFields.push({
                        key: personalInfo.linkedIn ? "linkedIn" : "linkedin",
                        label: `LinkedIn: ${linkedinValue}`,
                      });
                    }
                    if (personalInfo.website) {
                      availableFields.push({ key: "website", label: `Website: ${personalInfo.website}` });
                    }
                    if (personalInfo.github) {
                      availableFields.push({ key: "github", label: `GitHub: ${personalInfo.github}` });
                    }

                    return availableFields;
                  })()
                : null,
          },
          summary: { label: "Professional Summary", fields: null },
          experience: {
            label: "Work Experience",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.experience || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.experience || [];

                  return sortedData.map((exp, sortedIndex) => {
                    // Create a unique signature for matching
                    const expSignature = `${exp.title || ""}_${exp.company || ""}_${exp.startDate || ""}_${
                      exp.endDate || ""
                    }`;

                    // Find the original index by creating signatures for all original items
                    const originalIndex = originalData.findIndex((originalExp, idx) => {
                      const originalSignature = `${originalExp.title || ""}_${originalExp.company || ""}_${
                        originalExp.startDate || ""
                      }_${originalExp.endDate || ""}`;
                      const matches = expSignature === originalSignature;
                      if (matches) {
                      }
                      return matches;
                    });

                    if (originalIndex === -1) {
                      console.warn(`⚠️  No match found for experience: "${exp.title}" at "${exp.company}"`);
                    }

                    return {
                      key: originalIndex >= 0 ? originalIndex : sortedIndex, // Fallback to sorted index
                      label: `${exp.title || "(No Title)"} at ${exp.company || "(No Company)"}`,
                    };
                  });
                })()
              : [],
          },
          education: {
            label: "Education",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.education || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.education || [];

                  return sortedData.map((edu, sortedIndex) => {
                    // Create a unique signature for matching
                    const eduSignature = `${edu.degree || ""}_${edu.school || ""}_${edu.startDate || ""}_${
                      edu.endDate || ""
                    }`;

                    // Find the original index by creating signatures for all original items
                    const originalIndex = originalData.findIndex((originalEdu, idx) => {
                      const originalSignature = `${originalEdu.degree || ""}_${originalEdu.school || ""}_${
                        originalEdu.startDate || ""
                      }_${originalEdu.endDate || ""}`;
                      const matches = eduSignature === originalSignature;
                      if (matches) {
                      }
                      return matches;
                    });

                    if (originalIndex === -1) {
                      console.warn(`⚠️  No match found for education: "${edu.degree}" from "${edu.school}"`);
                    }

                    return {
                      key: originalIndex >= 0 ? originalIndex : sortedIndex, // Fallback to sorted index
                      label: `${edu.degree || "(No Degree)"} from ${edu.school || "(No School)"}`,
                    };
                  });
                })()
              : [],
          },
          projects: {
            label: "Projects",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.projects || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.projects || [];

                  return sortedData.map((project, sortedIndex) => {
                    // Find the original index of this item in the unsorted array
                    const originalIndex = originalData.findIndex(
                      (originalProject) =>
                        (originalProject.name === project.name || originalProject.title === project.title) &&
                        originalProject.description === project.description &&
                        JSON.stringify(originalProject.technologies) === JSON.stringify(project.technologies)
                    );

                    return {
                      key: originalIndex >= 0 ? originalIndex : sortedIndex, // Fallback to sorted index
                      label: `${project.name || project.title || "(No Project Name)"}${
                        project.technologies ? ` (${project.technologies.slice(0, 2).join(", ")})` : ""
                      }`,
                    };
                  });
                })()
              : [],
          },
          certifications: {
            label: "Certifications",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.certifications || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.certifications || [];

                  return sortedData.map((cert, sortedIndex) => {
                    // Create a unique signature for matching
                    const certSignature = `${cert.name || ""}_${cert.issuer || ""}_${cert.date || ""}`;

                    // Find the original index by creating signatures for all original items
                    const originalIndex = originalData.findIndex((originalCert, idx) => {
                      const originalSignature = `${originalCert.name || ""}_${originalCert.issuer || ""}_${
                        originalCert.date || ""
                      }`;
                      const matches = certSignature === originalSignature;
                      if (matches) {
                      }
                      return matches;
                    });

                    if (originalIndex === -1) {
                      console.warn(`⚠️  No match found for certification: "${cert.name}" from "${cert.issuer}"`);
                    }

                    return {
                      key: originalIndex >= 0 ? originalIndex : sortedIndex, // Fallback to sorted index
                      label: `${cert.name || "(No Certification Name)"}${
                        cert.issuer ? ` from ${cert.issuer}` : ""
                      }`,
                    };
                  });
                })()
              : [],
          },
          skills: { label: "Skills", fields: null },
        },
      },
      "@delete": {
        label: "Delete",
        subCommands: {
          experience: {
            label: "Work Experience",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.experience || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.experience || [];

                  const allFields = [];

                  sortedData.forEach((exp, sortedIndex) => {
                    // Create a unique signature for matching
                    const expSignature = `${exp.title || ""}_${exp.company || ""}_${exp.startDate || ""}_${
                      exp.endDate || ""
                    }`;

                    // Find the original index by creating signatures for all original items
                    const originalIndex = originalData.findIndex((originalExp, idx) => {
                      const originalSignature = `${originalExp.title || ""}_${originalExp.company || ""}_${
                        originalExp.startDate || ""
                      }_${originalExp.endDate || ""}`;
                      const matches = expSignature === originalSignature;
                      if (matches) {
                      }
                      return matches;
                    });

                    if (originalIndex === -1) {
                      console.warn(`⚠️  No match found for experience: "${exp.title}" at "${exp.company}"`);
                    }

                    const baseIndex = originalIndex >= 0 ? originalIndex : sortedIndex;

                    // Add option to delete entire experience entry
                    allFields.push({
                      key: baseIndex,
                      label: `[Entire Entry] ${exp.title || "(No Title)"} at ${exp.company || "(No Company)"}`,
                      isCheckbox: true,
                    });

                    // Add granular field options
                    if (exp.title) {
                      allFields.push({
                        key: `${baseIndex}_title`,
                        label: `Title: ${exp.title}`,
                        isCheckbox: true,
                      });
                    }
                    if (exp.company) {
                      allFields.push({
                        key: `${baseIndex}_company`,
                        label: `Company: ${exp.company}`,
                        isCheckbox: true,
                      });
                    }
                    if (exp.location) {
                      allFields.push({
                        key: `${baseIndex}_location`,
                        label: `Location: ${exp.location}`,
                        isCheckbox: true,
                      });
                    }
                    if (exp.startDate || exp.endDate) {
                      allFields.push({
                        key: `${baseIndex}_dates`,
                        label: `Dates: ${exp.startDate || ""} - ${exp.endDate || "Present"}`,
                        isCheckbox: true,
                      });
                    }
                    if (exp.description) {
                      allFields.push({
                        key: `${baseIndex}_description`,
                        label: `Description`,
                        isCheckbox: true,
                      });
                    }
                  });

                  return allFields;
                })()
              : [],
          },
          education: {
            label: "Education",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.education || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.education || [];

                  const allFields = [];

                  sortedData.forEach((edu, sortedIndex) => {
                    // Create a unique signature for matching
                    const eduSignature = `${edu.degree || ""}_${edu.school || ""}_${edu.startDate || ""}_${
                      edu.endDate || ""
                    }`;

                    // Find the original index by creating signatures for all original items
                    const originalIndex = originalData.findIndex((originalEdu, idx) => {
                      const originalSignature = `${originalEdu.degree || ""}_${originalEdu.school || ""}_${
                        originalEdu.startDate || ""
                      }_${originalEdu.endDate || ""}`;
                      const matches = eduSignature === originalSignature;
                      if (matches) {
                      }
                      return matches;
                    });

                    if (originalIndex === -1) {
                      console.warn(`⚠️  No match found for education: "${edu.degree}" from "${edu.school}"`);
                    }

                    const baseIndex = originalIndex >= 0 ? originalIndex : sortedIndex;

                    // Add option to delete entire education entry
                    allFields.push({
                      key: baseIndex,
                      label: `[Entire Entry] ${edu.degree || "(No Degree)"} from ${edu.school || "(No School)"}`,
                      isCheckbox: true,
                    });

                    // Add granular field options
                    if (edu.degree) {
                      allFields.push({
                        key: `${baseIndex}_degree`,
                        label: `Degree: ${edu.degree}`,
                        isCheckbox: true,
                      });
                    }
                    if (edu.school) {
                      allFields.push({
                        key: `${baseIndex}_school`,
                        label: `School: ${edu.school}`,
                        isCheckbox: true,
                      });
                    }
                    if (edu.field) {
                      allFields.push({
                        key: `${baseIndex}_field`,
                        label: `Field: ${edu.field}`,
                        isCheckbox: true,
                      });
                    }
                    if (edu.startDate || edu.endDate) {
                      allFields.push({
                        key: `${baseIndex}_dates`,
                        label: `Dates: ${edu.startDate || ""} - ${edu.endDate || "Present"}`,
                        isCheckbox: true,
                      });
                    }
                    if (edu.gpa) {
                      allFields.push({
                        key: `${baseIndex}_gpa`,
                        label: `GPA: ${edu.gpa}`,
                        isCheckbox: true,
                      });
                    }
                  });

                  return allFields;
                })()
              : [],
          },
          projects: {
            label: "Projects",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.projects || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.projects || [];

                  return sortedData.map((project, sortedIndex) => {
                    // Find the original index of this item in the unsorted array
                    const originalIndex = originalData.findIndex(
                      (originalProject) =>
                        (originalProject.name === project.name || originalProject.title === project.title) &&
                        originalProject.description === project.description &&
                        JSON.stringify(originalProject.technologies) === JSON.stringify(project.technologies)
                    );

                    return {
                      key: originalIndex >= 0 ? originalIndex : sortedIndex, // Fallback to sorted index
                      label: `${project.name || project.title || "(No Project Name)"}${
                        project.technologies ? ` (${project.technologies.slice(0, 2).join(", ")})` : ""
                      }`,
                    };
                  });
                })()
              : [],
          },
          certifications: {
            label: "Certifications",
            fields: resumeData?.parsedData
              ? (() => {
                  const originalData = resumeData.parsedData.certifications || [];
                  const sortedData = sortResumeForDisplay(resumeData.parsedData)?.certifications || [];

                  return sortedData.map((cert, sortedIndex) => {
                    // Create a unique signature for matching
                    const certSignature = `${cert.name || ""}_${cert.issuer || ""}_${cert.date || ""}`;

                    // Find the original index by creating signatures for all original items
                    const originalIndex = originalData.findIndex((originalCert, idx) => {
                      const originalSignature = `${originalCert.name || ""}_${originalCert.issuer || ""}_${
                        originalCert.date || ""
                      }`;
                      const matches = certSignature === originalSignature;
                      if (matches) {
                      }
                      return matches;
                    });

                    if (originalIndex === -1) {
                      console.warn(`⚠️  No match found for certification: "${cert.name}" from "${cert.issuer}"`);
                    }

                    return {
                      key: originalIndex >= 0 ? originalIndex : sortedIndex, // Fallback to sorted index
                      label: `${cert.name || "(No Certification Name)"}${
                        cert.issuer ? ` from ${cert.issuer}` : ""
                      }`,
                    };
                  });
                })()
              : [],
          },
          skills: {
            label: "Skills",
            fields:
              resumeData?.parsedData && resumeData.parsedData.skills
                ? (Array.isArray(resumeData.parsedData.skills)
                    ? resumeData.parsedData.skills
                    : resumeData.parsedData.skills?.list || []
                  ).map((skill, index) => ({
                    key: index,
                    label: skill,
                  }))
                : [],
          },
          personalInfo: {
            label: "Personal Information",
            fields:
              resumeData?.parsedData && resumeData.parsedData.personalInfo
                ? (() => {
                    const personalInfo = resumeData.parsedData.personalInfo;
                    const availableFields = [];

                    // Add available personal info fields that have data
                    if (personalInfo.name) {
                      availableFields.push({ key: "name", label: `Name: ${personalInfo.name}` });
                    }
                    if (personalInfo.email) {
                      availableFields.push({ key: "email", label: `Email: ${personalInfo.email}` });
                    }
                    if (personalInfo.phone) {
                      availableFields.push({ key: "phone", label: `Phone: ${personalInfo.phone}` });
                    }
                    if (personalInfo.location) {
                      availableFields.push({ key: "location", label: `Location: ${personalInfo.location}` });
                    }
                    if (personalInfo.linkedIn || personalInfo.linkedin) {
                      const linkedinValue = personalInfo.linkedIn || personalInfo.linkedin;
                      availableFields.push({
                        key: personalInfo.linkedIn ? "linkedIn" : "linkedin",
                        label: `LinkedIn: ${linkedinValue}`,
                      });
                    }
                    if (personalInfo.website) {
                      availableFields.push({ key: "website", label: `Website: ${personalInfo.website}` });
                    }
                    if (personalInfo.github) {
                      availableFields.push({ key: "github", label: `GitHub: ${personalInfo.github}` });
                    }

                    return availableFields;
                  })()
                : [],
          },
          summary: {
            label: "Professional Summary",
            fields: null, // No fields needed for summary - delete entire section
          },
        },
      },
    };
  };

  // Get current command structure
  const commandStructure = getCommandStructure();

  useEffect(() => {
    // Load chat history or add initial AI message
    const initializeChat = async () => {
      const existingMessages = await loadChatHistory();
      if (existingMessages.length > 0) {
        setMessages(existingMessages);
      } else {
        // Check if this is a blank resume (created from scratch)
        const isBlankResume = resumeData?.creationMode === "blank";

        if (isBlankResume && resumeData?.initialContext) {
          // Create contextual welcome message for blank resumes
          const { targetRole, experienceLevel, industry, userBackground } = resumeData.initialContext;

          // Generate contextual starter prompts based on the target role and experience
          const starterPrompts = [
            `📝 Let's start with your personal information (name, email, phone, location)`,
            `💼 Tell me about your work experience relevant to ${targetRole}`,
            `🎯 Help me write a compelling professional summary for ${targetRole}`,
            `⚡ What are your key skills for ${targetRole}?`,
            `🎓 Let's add your educational background`,
          ];

          // Add role-specific suggestions
          const roleSpecificPrompts = [];
          if (experienceLevel === "entry") {
            roleSpecificPrompts.push(`🌟 Let's highlight your internships, projects, and coursework`);
            roleSpecificPrompts.push(`🏆 What achievements or awards can we showcase?`);
          } else if (experienceLevel === "senior" || experienceLevel === "executive") {
            roleSpecificPrompts.push(`🏆 Let's emphasize your leadership experience and achievements`);
            roleSpecificPrompts.push(`📈 What major projects or initiatives have you led?`);
          }

          const initialMessage = [
            {
              id: "blank-welcome-" + Date.now().toString(),
              type: "ai",
              content: `🚀 Welcome! I'm excited to help you build your ${targetRole} resume from scratch!\n\n**Based on your setup:**\n• Target Role: ${targetRole}\n• Experience Level: ${experienceLevel}\n${
                industry ? `• Industry: ${industry}\n` : ""
              }${
                userBackground ? `• Background: ${userBackground}\n` : ""
              }\n**Let's build your resume step by step:**`,
              suggestions: [
                ...starterPrompts,
                ...roleSpecificPrompts,
                "💡 Type '/help' anytime for detailed usage instructions",
                "📄 Click 'Preview Resume' to see your progress anytime",
              ],
              timestamp: new Date().toISOString(),
            },
          ];

          setMessages(initialMessage);
          await saveChatHistory(initialMessage);
        } else {
          // Regular uploaded resume flow
          const quickIssues = generateQuickIssues(resumeData);
          const initialMessage = [
            {
              id: "welcome-v2-" + Date.now().toString(),
              type: "ai",
              content:
                "Hello! I'm your AI Resume Assistant. I've analyzed your resume and found specific areas that need attention.\n\n**Quick Issues:**",
              suggestions: [
                ...quickIssues.filter((issue) => issue && issue.trim().length > 0),
                "💡 Type '/help' anytime for detailed usage instructions",
              ],
              timestamp: new Date().toISOString(),
            },
          ];
          setMessages(initialMessage);
          await saveChatHistory(initialMessage);
        }
      }
    };

    initializeChat();
  }, [resumeId]);

  // Auto-scroll to bottom when messages change or loading state changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, isChatLoading]);

  // Auto-scroll on window resize to handle dynamic content
  useEffect(() => {
    const handleResize = () => {
      scrollToBottom();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset selected fields when resume data changes (after accepting changes)
  useEffect(() => {
    if (selectedCommand && selectedSubCommand) {
      // Reset field selection when resume data changes
      // This ensures the dropdown gets updated with new items
      setSelectedField(null);
    }
  }, [resumeData]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleInputChange = (value) => {
    setInputValue(value);
  };

  // Helper function to build comprehensive contextual data for AI
  const buildContextualData = () => {
    if (!resumeData?.parsedData) return null;

    const data = resumeData.parsedData;
    const contextualData = {
      ...data,
      // Add meta information about the current command context
      _commandContext: {
        command: selectedCommand,
        subCommand: selectedSubCommand,
        selectedField: selectedField,
        availableFields: commandStructure[selectedCommand]?.subCommands[selectedSubCommand]?.fields || null,
      },
      // Add counts and summaries for better AI understanding
      _dataSummary: {
        totalExperience: Array.isArray(data.experience) ? data.experience.length : 0,
        totalEducation: Array.isArray(data.education) ? data.education.length : 0,
        totalSkills: Array.isArray(data.skills)
          ? data.skills.length
          : data.skills?.list
          ? data.skills.list.length
          : 0,
        totalProjects: Array.isArray(data.projects) ? data.projects.length : 0,
        totalCertifications: Array.isArray(data.certifications) ? data.certifications.length : 0,
        hasPersonalInfo: !!(data.personalInfo && Object.keys(data.personalInfo).length > 0),
        hasSummary: !!(
          (typeof data.summary === "string" && data.summary.trim().length > 0) ||
          (data.summary?.content && data.summary.content.trim().length > 0)
        ),
      },
    };

    // Add specific context based on the command
    if (selectedCommand === "@add") {
      // For add commands, provide examples from existing data
      if (selectedSubCommand === "experience" && data.experience && data.experience.length > 0) {
        contextualData._contextualExamples = {
          existingExperience: data.experience.slice(0, 2), // Provide 2 most recent examples
        };
      } else if (selectedSubCommand === "education" && data.education && data.education.length > 0) {
        contextualData._contextualExamples = {
          existingEducation: data.education.slice(0, 2),
        };
      } else if (selectedSubCommand === "skills") {
        const skillsList = Array.isArray(data.skills) ? data.skills : data.skills?.list || [];
        if (skillsList.length > 0) {
          contextualData._contextualExamples = {
            existingSkills: skillsList.slice(0, 10), // Show some existing skills
          };
        }
      } else if (selectedSubCommand === "projects" && data.projects && data.projects.length > 0) {
        contextualData._contextualExamples = {
          existingProjects: data.projects.slice(0, 2), // Provide 2 most recent examples
        };
      } else if (
        selectedSubCommand === "certifications" &&
        data.certifications &&
        data.certifications.length > 0
      ) {
        contextualData._contextualExamples = {
          existingCertifications: data.certifications.slice(0, 2), // Provide 2 most recent examples
        };
      }
    } else if (selectedCommand === "@enhance" && selectedField !== null) {
      // For enhance commands, provide the specific item being enhanced
      const fields = commandStructure[selectedCommand].subCommands[selectedSubCommand].fields;
      if (fields && fields[selectedField]) {
        const fieldKey = fields[selectedField].key;

        if (selectedSubCommand === "experience" && data.experience && data.experience[fieldKey]) {
          contextualData._currentItem = {
            type: "experience",
            index: fieldKey,
            data: data.experience[fieldKey],
          };
        } else if (selectedSubCommand === "education" && data.education && data.education[fieldKey]) {
          contextualData._currentItem = {
            type: "education",
            index: fieldKey,
            data: data.education[fieldKey],
          };
        } else if (selectedSubCommand === "projects" && data.projects && data.projects[fieldKey]) {
          contextualData._currentItem = {
            type: "projects",
            index: fieldKey,
            data: data.projects[fieldKey],
          };
        } else if (
          selectedSubCommand === "certifications" &&
          data.certifications &&
          data.certifications[fieldKey]
        ) {
          contextualData._currentItem = {
            type: "certifications",
            index: fieldKey,
            data: data.certifications[fieldKey],
          };
        }
      }
    }

    return contextualData;
  };

  const handleSend = async () => {
    if (!inputValue.trim() && selectedCommand !== "@delete") return;

    // Handle /help command
    if (inputValue.trim() === "/help") {
      const helpMessage = {
        id: Date.now().toString(),
        type: "user",
        content: "/help",
        timestamp: new Date().toISOString(),
      };

      const helpResponse = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content: "Here's a comprehensive guide on how to use the Resume AI Assistant:",
        helpContent: {
          title: "📚 Resume AI Assistant - Usage Guide",
          sections: [
            {
              title: "🎯 Available Commands",
              content: [
                "• **@add** - Add new sections to your resume",
                "• **@enhance** - Improve existing resume sections",
                "• **@delete** - Remove sections (coming soon)",
              ],
            },
            {
              title: "➕ @add Commands",
              content: [
                "**@add experience** - Add new work experience",
                "**@add education** - Add new education entry",
                "**@add projects** - Add new project",
                "**@add skills** - Add new skills",
                "**@add certifications** - Add new certification",
                "**@add summary** - Add professional summary",
              ],
              examples: [
                {
                  command: "@add experience",
                  input:
                    "Software Engineer at TechCorp from Jan 2022 to present. Developed web applications using React and Node.js, improved system performance by 40%",
                  description:
                    "The AI will extract job title, company, dates, and create a professional description",
                },
                {
                  command: "@add education",
                  input: "Bachelor of Computer Science from Stanford University, graduated 2021 with 3.8 GPA",
                  description: "The AI will format degree, school, graduation date, and GPA",
                },
                {
                  command: "@add projects",
                  input:
                    "E-commerce website built with React, Node.js, and MongoDB. Features include user authentication, payment processing",
                  description: "The AI will create project name, description, and technology list",
                },
              ],
            },
            {
              title: "✨ @enhance Commands",
              content: [
                "**@enhance experience** - Improve existing work experience",
                "**@enhance education** - Enhance education entries",
                "**@enhance projects** - Improve project descriptions",
                "**@enhance certifications** - Enhance certifications",
                "**@enhance summary** - Improve professional summary",
                "**@enhance skills** - Optimize skills",
              ],
              note: "💡 For @enhance commands, you'll need to select which specific item to enhance from the dropdown",
              examples: [
                {
                  command: "@enhance experience (Software Engineer at TechCorp)",
                  input:
                    "Add more technical details about the microservices architecture I built and the 40% performance improvement",
                  description: "The AI will enhance the existing experience with more technical details",
                },
                {
                  command: "@enhance summary",
                  input: "Make it more focused on full-stack development and leadership experience",
                  description: "The AI will rewrite your professional summary with the requested focus",
                },
              ],
            },
            {
              title: "💡 Best Practices for Input",
              content: [
                "• **Be specific**: Include dates, company names, technologies, achievements",
                '• **Use metrics**: "Improved performance by 40%" is better than "improved performance"',
                "• **Include context**: Mention team size, project scope, technologies used",
                "• **Be clear about changes**: For @enhance, clearly state what you want to improve",
              ],
            },
            {
              title: "📝 Example Inputs That Work Well",
              content: [
                '**Good**: "Senior Developer at Microsoft from 2020-2023. Led team of 5 developers building cloud services with Azure, reduced deployment time by 60%"',
                '**Bad**: "Worked at Microsoft as developer"',
                "",
                '**Good**: "Master\'s in Data Science from MIT, graduated 2019, thesis on machine learning for healthcare"',
                '**Bad**: "MIT graduate"',
                "",
                '**Good**: "Enhance my React project description to emphasize the scalability improvements and user experience features"',
                '**Bad**: "Make it better"',
              ],
            },
            {
              title: "🔄 How It Works",
              content: [
                "1. **Select a command** from the dropdown (@add, @enhance)",
                "2. **Choose the section** you want to work with",
                "3. **For @enhance**: Select the specific item to improve",
                "4. **Write your input** with specific details",
                "5. **Review the AI suggestions** before accepting",
                "6. **Accept or reject** the proposed changes",
              ],
            },
            {
              title: "⚡ Pro Tips",
              content: [
                "• Your existing resume data helps the AI understand your style and format",
                "• The AI maintains consistency with your existing entries",
                "• You can enhance newly added items immediately after accepting them",
                "• Use specific job titles, technologies, and achievement metrics",
                "• The AI learns from your existing resume to maintain consistency",
              ],
            },
          ],
        },
        timestamp: new Date().toISOString(),
      };

      const updatedMessages = [...messages, helpMessage, helpResponse];
      setMessages(updatedMessages);
      // Note: /help commands are not saved to database to keep chat history clean

      // Clear input
      setInputValue("");
      return;
    }

    // Handle delete command - show inline confirmation in chat
    if (selectedCommand === "@delete") {
      // Handle single field delete
      if (selectedField !== null && selectedDeleteItems.length === 0) {
        const fields = commandStructure[selectedCommand].subCommands[selectedSubCommand].fields;
        const selectedFieldInfo = fields[selectedField];

        const deleteAction = {
          commandString: `${selectedCommand}-${selectedSubCommand}-${selectedFieldInfo?.key}`,
          commandPart: `${selectedCommand} ${selectedSubCommand} (${selectedFieldInfo?.label})`,
          section: selectedSubCommand,
          itemLabel: selectedFieldInfo?.label,
          fieldKey: selectedFieldInfo?.key,
          isMultiple: false,
        };

        // Add user message
        const userMessage = {
          id: Date.now().toString(),
          type: "user",
          content: `Delete ${selectedFieldInfo?.label} from ${selectedSubCommand}`,
          commandPart: deleteAction.commandPart,
          userInput: "Delete request",
          timestamp: new Date().toISOString(),
        };

        // Add confirmation message from AI
        const confirmationMessage = {
          id: (Date.now() + 1).toString(),
          type: "ai",
          content: `Are you sure you want to delete: ${selectedFieldInfo?.label}?`,
          isDeleteConfirmation: true,
          pendingDeleteAction: deleteAction,
          timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...messages, userMessage, confirmationMessage];
        setMessages(updatedMessages);
        await saveMessageToChat(userMessage);
        await saveMessageToChat(confirmationMessage);

        // Reset form
        resetCommand();
        setInputValue("");
        return;
      }
      // Handle multi-select delete
      else if (selectedDeleteItems.length > 0) {
        const fields = commandStructure[selectedCommand].subCommands[selectedSubCommand].fields;
        const selectedItemsInfo = selectedDeleteItems.map((index) => ({
          index: fields[index]?.key ?? index,
          label: fields[index]?.label,
        }));

        const deleteLabels = selectedItemsInfo.map((item) => item.label).join(", ");

        const deleteAction = {
          commandString: `${selectedCommand}-${selectedSubCommand}-multiple`,
          commandPart: `${selectedCommand} ${selectedSubCommand} (${selectedDeleteItems.length} items)`,
          section: selectedSubCommand,
          itemLabel: deleteLabels,
          multipleItems: selectedItemsInfo,
          isMultiple: true,
        };

        // Add user message
        const userMessage = {
          id: Date.now().toString(),
          type: "user",
          content: `Delete ${selectedDeleteItems.length} items from ${selectedSubCommand}`,
          commandPart: deleteAction.commandPart,
          userInput: "Delete request",
          timestamp: new Date().toISOString(),
        };

        // Add confirmation message from AI
        const confirmationMessage = {
          id: (Date.now() + 1).toString(),
          type: "ai",
          content: `Are you sure you want to delete these ${selectedDeleteItems.length} items?\n\n${deleteLabels}`,
          isDeleteConfirmation: true,
          pendingDeleteAction: deleteAction,
          timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...messages, userMessage, confirmationMessage];
        setMessages(updatedMessages);
        await saveMessageToChat(userMessage);
        await saveMessageToChat(confirmationMessage);

        // Clear form after showing confirmation
        setInputValue("");
        resetCommand();
        return;
      }

      // Single item delete (backward compatibility)
      const fieldIndexToUse =
        selectedField !== null
          ? commandStructure[selectedCommand].subCommands[selectedSubCommand].fields?.[selectedField]?.key ??
            selectedField
          : null;

      const deleteAction = {
        commandString: `${selectedCommand}-${selectedSubCommand}${
          fieldIndexToUse !== null ? `-${fieldIndexToUse}` : ""
        }`,
        commandPart: `${selectedCommand} ${selectedSubCommand}${
          selectedField !== null
            ? ` (${commandStructure[selectedCommand].subCommands[selectedSubCommand].fields[selectedField]?.label})`
            : ""
        }`,
        section: selectedSubCommand,
        itemLabel:
          selectedField !== null
            ? commandStructure[selectedCommand].subCommands[selectedSubCommand].fields[selectedField]?.label
            : selectedSubCommand,
      };

      // Add user message
      const userMessage = {
        id: Date.now().toString(),
        type: "user",
        content: deleteAction.commandPart,
        commandPart: deleteAction.commandPart,
        userInput: "Delete request",
        timestamp: new Date().toISOString(),
      };

      // Add confirmation message from AI
      const confirmationMessage = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content: `Are you sure you want to delete this item?`,
        isDeleteConfirmation: true,
        pendingDeleteAction: deleteAction,
        timestamp: new Date().toISOString(),
      };

      const updatedMessages = [...messages, userMessage, confirmationMessage];
      setMessages(updatedMessages);
      await saveMessageToChat(userMessage);
      await saveMessageToChat(confirmationMessage);

      // Clear form after showing confirmation
      setInputValue("");
      resetCommand();
      return;
    }

    // Regular command validation
    if (!selectedCommand || !selectedSubCommand) return;

    // For @enhance commands with fields, require field selection
    if (
      selectedCommand === "@enhance" &&
      commandStructure[selectedCommand].subCommands[selectedSubCommand].fields &&
      selectedField === null
    ) {
      return;
    }

    // For @delete commands with fields, require field selection
    if (
      selectedCommand === "@delete" &&
      commandStructure[selectedCommand].subCommands[selectedSubCommand].fields &&
      selectedField === null
    ) {
      return;
    }

    // For enhance and delete commands, use the mapped original index instead of selectedField
    let fieldIndexToUse = selectedField;
    if ((selectedCommand === "@enhance" || selectedCommand === "@delete") && selectedField !== null) {
      const fields = commandStructure[selectedCommand].subCommands[selectedSubCommand].fields;
      if (fields && fields[selectedField]) {
        fieldIndexToUse = fields[selectedField].key; // Use the mapped original index
      }
    }

    const commandString = `${selectedCommand}-${selectedSubCommand}${
      selectedField !== null ? `-${fieldIndexToUse}` : ""
    }`;

    const commandPart = `${selectedCommand} ${selectedSubCommand}${
      selectedField !== null
        ? ` (${commandStructure[selectedCommand].subCommands[selectedSubCommand].fields[selectedField]?.label})`
        : ""
    }`;

    const userMessage = {
      id: Date.now().toString(),
      type: "user",
      content: `${commandPart}: ${inputValue}`,
      commandPart: commandPart,
      userInput: inputValue,
      timestamp: new Date().toISOString(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    await saveMessageToChat(userMessage);

    // Start local chat loading
    setIsChatLoading(true);

    try {
      // Build comprehensive contextual data for better AI responses
      const contextualData = buildContextualData();

      const result = await onSendCommand(commandString, inputValue, contextualData);

      // Check if there was an error in processing
      if (result.error) {
        const errorMessage = {
          id: (Date.now() + 1).toString(),
          type: "ai",
          content: result.message || "Sorry, I encountered an error processing your request.",
          error: result.error,
          changesMade: result.changesMade || ["Error occurred during processing"],
          timestamp: new Date().toISOString(),
        };
        const finalMessages = [...updatedMessages, errorMessage];
        setMessages(finalMessages);
        await saveMessageToChat(errorMessage);
        return;
      }

      const aiResponse = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content: result.message || "I've processed your request and prepared the changes.",
        proposedChanges: result.proposedChanges || [],
        fieldsStayingSame: result.fieldsStayingSame || [],
        // Use pendingChanges from backend (it already has the correct structure)
        pendingChanges: result.pendingChanges,
        // Add additional fields from pendingChanges for preview (extract from correct location)
        action: result.pendingChanges?.action,
        section: result.pendingChanges?.section,
        data: result.pendingChanges?.data,
        targetIndex: result.pendingChanges?.targetIndex,
        timestamp: new Date().toISOString(),
      };

      const finalMessages = [...updatedMessages, aiResponse];
      setMessages(finalMessages);
      await saveMessageToChat(aiResponse);
      setPendingChanges(result.pendingChanges);
    } catch (error) {
      console.error("Command processing error", error.message || error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content: "Sorry, I couldn't process that request. Please check your connection and try again.",
        error: error.message,
        changesMade: ["Network or server error occurred"],
        timestamp: new Date().toISOString(),
      };

      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      await saveMessageToChat(errorMessage);
    } finally {
      // Stop local chat loading
      setIsChatLoading(false);
      // Clear form after processing
      setInputValue("");
      resetCommand();
    }
  };

  const handleAcceptChanges = async (changes, proposedChanges) => {
    try {
      // Apply changes to resume data (use current resume data, not contextual data)
      await onSendCommand("apply-changes", { changes, proposedChanges }, resumeData?.parsedData);

      // Update the message to show accepted with persistent state
      const updatedMessages = messages.map((msg) => {
        if (msg.pendingChanges === changes) {
          return {
            ...msg,
            changesAccepted: true,
            changesRejected: false,
            // Add unique identifier for this action to prevent re-showing
            actionId: `accepted_${msg.id}_${Date.now()}`,
            actionTimestamp: new Date().toISOString(),
          };
        }
        return msg;
      });
      setMessages(updatedMessages);
      await saveChatHistory(updatedMessages);

      setPendingChanges(null);
    } catch (error) {
      console.error("Error applying changes", error.message || error);
    }
  };

  const handleRejectChanges = async (changes) => {
    // Update the message to show rejected with persistent state
    const updatedMessages = messages.map((msg) => {
      if (msg.pendingChanges === changes) {
        return {
          ...msg,
          changesRejected: true,
          changesAccepted: false,
          // Add unique identifier for this action to prevent re-showing
          actionId: `rejected_${msg.id}_${Date.now()}`,
          actionTimestamp: new Date().toISOString(),
        };
      }
      return msg;
    });
    setMessages(updatedMessages);
    await saveChatHistory(updatedMessages);

    setPendingChanges(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const resetCommand = () => {
    setSelectedCommand(null);
    setSelectedSubCommand(null);
    setSelectedField(null);
    setSelectedDeleteItems([]);
    setShowDropdown(false);
  };

  const handleConfirmDelete = async (deleteAction, messageId) => {
    let finalMessages; // Declare at function scope
    try {
      // Update the confirmation message to show loading
      const updatedMessages = messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              content: deleteAction.isMultiple ? "Deleting items..." : "Deleting item...",
              isDeleting: true,
            }
          : msg
      );
      setMessages(updatedMessages);

      // Handle multiple deletions
      if (deleteAction.isMultiple && deleteAction.multipleItems) {
        const results = [];
        for (const item of deleteAction.multipleItems) {
          const commandString = `@delete-${deleteAction.section}-${item.index}`;
          try {
            const result = await onSendCommand(commandString, "", resumeData?.parsedData);
            // Check if the result indicates success (either by having a message or not throwing an error)
            const isSuccess = result && (result.message || result.deletedSection || true);
            results.push({ success: isSuccess, item: item.label });
          } catch (error) {
            console.error(`Failed to delete ${item.label}`, error.message || error, { itemLabel: item.label });
            results.push({ success: false, item: item.label, error: error.message });
          }
        }

        const successCount = results.filter((r) => r.success).length;
        const failCount = results.filter((r) => !r.success).length;

        finalMessages = messages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                content: `✅ Successfully deleted ${successCount} item${successCount !== 1 ? "s" : ""}${
                  failCount > 0 ? ` (${failCount} failed)` : ""
                }`,
                isDeleteConfirmation: false,
                isDeleting: false,
                isDeleteSuccess: true,
              }
            : msg
        );
        setMessages(finalMessages);
      } else {
        // Single item delete
        const result = await onSendCommand(deleteAction.commandString, "", resumeData?.parsedData);

        finalMessages = messages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                content: result.message || `✅ Successfully deleted ${deleteAction.itemLabel}`,
                isDeleteConfirmation: false,
                isDeleting: false,
                isDeleteSuccess: true,
              }
            : msg
        );
        setMessages(finalMessages);
      }

      // Save the updated message
      const successMessage = finalMessages.find((msg) => msg.id === messageId);
      await saveMessageToChat(successMessage);
    } catch (error) {
      console.error("Delete operation failed", error.message || error);

      // Replace confirmation message with error message
      const errorMessages = messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              content: `❌ Failed to delete ${deleteAction.itemLabel}: ${error.message}`,
              isDeleteConfirmation: false,
              isDeleting: false,
              isDeleteError: true,
            }
          : msg
      );
      setMessages(errorMessages);

      // Save the error message
      const errorMessage = errorMessages.find((msg) => msg.id === messageId);
      await saveMessageToChat(errorMessage);
    }
  };

  const handleCancelDelete = async (messageId) => {
    // Replace confirmation message with cancelled message
    const cancelledMessages = messages.map((msg) =>
      msg.id === messageId
        ? {
            ...msg,
            content: "Delete operation cancelled.",
            isDeleteConfirmation: false,
            isDeleteCancelled: true,
          }
        : msg
    );
    setMessages(cancelledMessages);

    // Save the cancelled message
    const cancelledMessage = cancelledMessages.find((msg) => msg.id === messageId);
    await saveMessageToChat(cancelledMessage);
  };

  // Removed terminate process function - no loading states to handle

  const isFormValid = () => {
    // Allow /help command without command selection
    if (inputValue.trim() === "/help") return true;

    // Regular command validation
    if (!selectedCommand || !selectedSubCommand) return false;
    if (
      selectedCommand === "@enhance" &&
      commandStructure[selectedCommand].subCommands[selectedSubCommand].fields &&
      selectedField === null
    )
      return false;
    if (
      selectedCommand === "@delete" &&
      commandStructure[selectedCommand].subCommands[selectedSubCommand].fields &&
      selectedField === null &&
      selectedDeleteItems.length === 0
    )
      return false;

    // Delete commands don't require input text, just command and field/items selection
    if (selectedCommand === "@delete") {
      // Check if the selected section has fields
      const hasFields = commandStructure[selectedCommand].subCommands[selectedSubCommand].fields !== null;

      if (hasFields) {
        // For sections with fields, require field or item selection
        return selectedDeleteItems.length > 0 || selectedField !== null;
      } else {
        // For sections without fields (like summary), allow deletion without field selection
        return true;
      }
    }

    return inputValue.trim().length > 0;
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#fafbfc",
      }}
    >
      {/* Chat Messages - Fixed height with auto-scroll */}
      <div
        ref={chatContainerRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px",
          minHeight: 0,
          paddingTop: "12px",
          scrollBehavior: "smooth",
        }}
      >
        {messages.map((message) => (
          <div key={message.id} style={{ marginBottom: "12px" }}>
            {message.type === "ai" ? (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  maxWidth: "100%",
                  marginBottom: "4px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "4px",
                    backgroundColor: "#374151",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: "12px",
                  }}
                >
                  🤖
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: "#374151",
                      marginBottom:
                        message.proposedChanges && message.proposedChanges.length > 0 ? "14px" : "10px",
                    }}
                  >
                    {message.content}
                  </div>

                  {/* Enhanced Resume Preview - Integrated into main container */}
                  {message.proposedChanges && message.proposedChanges.length > 0 && (
                    <div
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "12px",
                        marginTop: "0px",
                      }}
                    >
                      {/* Header with sparkles icon */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            backgroundColor: "#f0f9ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                          }}
                        >
                          ✨
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          Here's how your resume will look with my enhancements:
                        </div>
                      </div>

                      {/* Resume Content Preview */}
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #f3f4f6",
                          borderRadius: "6px",
                          padding: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        {/* Display content in resume format like right panel */}
                        <div style={{ marginBottom: "8px" }}>
                          {message.proposedChanges && message.proposedChanges.length > 0
                            ? (() => {
                                // Group ALL fields (both changed and unchanged) to show complete item
                                const changes = {};

                                // Add changed fields
                                message.proposedChanges.forEach((change) => {
                                  changes[change.field] = change.after;
                                });

                                // Add unchanged fields to show complete item preview
                                if (message.fieldsStayingSame && Array.isArray(message.fieldsStayingSame)) {
                                  message.fieldsStayingSame.forEach((field) => {
                                    if (field.field && !changes[field.field]) {
                                      // Use field.value only if it exists, don't provide default
                                      if (field.value) {
                                        changes[field.field] = field.value;
                                      }
                                    }
                                  });
                                }

                                // Always format in resume style - no fallback to raw display

                                // Debug logging for enhance-summary
                                if (message.section === "summary" && message.action === "enhance") {
                                }

                                // Check for content in multiple sources for better hasAnyContent detection
                                const hasAnyContent =
                                  Object.keys(changes).length > 0 ||
                                  (message.section === "summary" &&
                                    (message.data ||
                                      (message.proposedChanges && message.proposedChanges.length > 0)));

                                return (
                                  <div
                                    style={{
                                      fontFamily: "Arial, sans-serif",
                                      fontSize: "12px",
                                      lineHeight: "1.4",
                                      color: "#333",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "4px",
                                      padding: "12px",
                                    }}
                                  >
                                    {/* DYNAMIC PREVIEW - Show actual fields with change indicators */}
                                    {hasAnyContent ? (
                                      <div>
                                        {/* Check if this is an enhance command - Show organized boxes */}
                                        {message.fieldsStayingSame &&
                                        message.fieldsStayingSame.length > 0 &&
                                        message.action !== "add" ? (
                                          <div>
                                            {/* Changes Made Box with actual content */}
                                            <div
                                              style={{
                                                backgroundColor: "#f8f9fa",
                                                border: "1px solid #d1d5db",
                                                borderRadius: "6px",
                                                padding: "8px 10px",
                                                marginBottom: "8px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "14px",
                                                  fontWeight: "600",
                                                  color: "#374151",
                                                  marginBottom: "8px",
                                                }}
                                              >
                                                📝 Changes Made:
                                              </div>
                                              {/* Debug: Show if proposedChanges exist */}
                                              {message.section === "summary" && (
                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    color: "#666",
                                                    marginBottom: "8px",
                                                    fontFamily: "monospace",
                                                  }}
                                                >
                                                  DEBUG: proposedChanges length ={" "}
                                                  {message.proposedChanges?.length || 0}
                                                </div>
                                              )}
                                              {message.proposedChanges && message.proposedChanges.length > 0
                                                ? message.proposedChanges.map((change) => {
                                                    // Handle summary field specially with comprehensive fallback
                                                    let value = changes[change.field];

                                                    if (!value && message.section === "summary") {
                                                      // Try all possible data sources for summary
                                                      value =
                                                        changes.summary ||
                                                        changes.content ||
                                                        message.data ||
                                                        change.after ||
                                                        (typeof message.data === "object" &&
                                                          message.data?.content) ||
                                                        (typeof message.data === "string" ? message.data : null);
                                                    }

                                                    if (!value) {
                                                      if (message.section === "summary") {
                                                        // Show fallback message for summary if no data found but proposedChanges exist
                                                        value =
                                                          change.after || `Enhanced summary for ${change.field}`;
                                                      } else {
                                                        return null;
                                                      }
                                                    }

                                                    // Extract text content for display (handle object structures)
                                                    let displayValue = value;
                                                    if (typeof value === "object" && value !== null) {
                                                      if (value.content) {
                                                        displayValue = value.content; // Extract content from object structure
                                                      } else if (value.data) {
                                                        displayValue = value.data; // Extract data field
                                                      } else {
                                                        // For objects without content/data field, use change.after or fallback
                                                        displayValue = change.after || "Content updated";
                                                      }
                                                    }

                                                    return (
                                                      <div
                                                        key={change.field}
                                                        style={{
                                                          fontSize: "13px",
                                                          color: "#4b5563",
                                                          marginLeft: "10px",
                                                          marginBottom: "4px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            fontWeight: "600",
                                                            textTransform: "capitalize",
                                                          }}
                                                        >
                                                          {change.field.replace(/([A-Z])/g, " $1").toLowerCase()}:
                                                        </span>{" "}
                                                        {change.field.toLowerCase().includes("description") &&
                                                        String(displayValue).includes("•") ? (
                                                          <div style={{ marginLeft: "8px", marginTop: "2px" }}>
                                                            {String(displayValue)
                                                              .split("\n")
                                                              .map((line, idx) => (
                                                                <div key={idx} style={{ marginBottom: "1px" }}>
                                                                  {line.trim()}
                                                                </div>
                                                              ))}
                                                          </div>
                                                        ) : (
                                                          <span>{String(displayValue)}</span>
                                                        )}
                                                      </div>
                                                    );
                                                  })
                                                : message.section === "summary" && (
                                                    <div
                                                      style={{
                                                        fontSize: "13px",
                                                        color: "#666",
                                                        fontStyle: "italic",
                                                      }}
                                                    >
                                                      No proposedChanges found, but summary data may be in
                                                      message.data: {JSON.stringify(message.data)}
                                                    </div>
                                                  )}
                                            </div>

                                            {/* Remaining Fields Box (what stays the same) */}
                                            <div
                                              style={{
                                                backgroundColor: "#f1f5f9",
                                                border: "1px solid #cbd5e1",
                                                borderRadius: "6px",
                                                padding: "8px 10px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "14px",
                                                  fontWeight: "600",
                                                  color: "#374151",
                                                  marginBottom: "8px",
                                                }}
                                              >
                                                📋 Fields Stay Same:
                                              </div>
                                              {message.fieldsStayingSame.map((field) => {
                                                // Use field.value as fallback if changes[field.field] is not available, but no default text
                                                const value = changes[field.field] || field.value || "";

                                                return (
                                                  <div
                                                    key={field.field}
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#64748b",
                                                      marginLeft: "10px",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span
                                                      style={{ fontWeight: "600", textTransform: "capitalize" }}
                                                    >
                                                      {field.field.replace(/([A-Z])/g, " $1").toLowerCase()}:
                                                    </span>{" "}
                                                    <span>{String(value)}</span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        ) : (
                                          // Regular add/update format - single cohesive preview
                                          <div>
                                            {(changes.summary || message.section === "summary") && (
                                              <div style={{ marginBottom: "10px" }}>
                                                <div
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    fontWeight: "600",
                                                    marginBottom: "4px",
                                                  }}
                                                >
                                                  {message.action === "add"
                                                    ? "Adding New Summary:"
                                                    : message.action === "enhance"
                                                    ? "Enhanced Summary:"
                                                    : "Summary:"}
                                                </div>
                                                <div
                                                  style={{ fontSize: "13px", color: "#4b5563", lineHeight: "1.5" }}
                                                >
                                                  {(() => {
                                                    // For both add-summary and enhance-summary commands, use message.data
                                                    const summaryData = changes.summary || message.data;

                                                    // Also check proposedChanges for summary field
                                                    if (
                                                      message.proposedChanges &&
                                                      message.proposedChanges.length > 0
                                                    ) {
                                                      const summaryChange = message.proposedChanges.find(
                                                        (change) =>
                                                          change.field === "summary" || change.field === "content"
                                                      );
                                                      if (summaryChange && summaryChange.after) {
                                                        return summaryChange.after;
                                                      }
                                                    }

                                                    if (summaryData) {
                                                      if (typeof summaryData === "string") {
                                                        return summaryData;
                                                      } else if (
                                                        typeof summaryData === "object" &&
                                                        summaryData.content
                                                      ) {
                                                        // Handle object format with content field
                                                        return summaryData.content;
                                                      } else if (
                                                        typeof summaryData === "object" &&
                                                        !summaryData.content
                                                      ) {
                                                        // Object format but no content field, convert to string
                                                        return String(summaryData);
                                                      }
                                                    }

                                                    // Enhanced fallback for enhance-summary - show something always
                                                    if (message.section === "summary") {
                                                      if (message.action === "enhance") {
                                                        return "Enhanced summary content (preview not available but changes will be applied)";
                                                      } else if (message.action === "add") {
                                                        return "Added summary content (preview not available but changes will be applied)";
                                                      }
                                                      return "Summary content will be modified";
                                                    }

                                                    return "Summary content";
                                                  })()}
                                                </div>
                                              </div>
                                            )}

                                            {(changes.title ||
                                              changes.company ||
                                              changes.location ||
                                              changes.startDate ||
                                              changes.endDate) && (
                                              <div style={{ marginBottom: "8px" }}>
                                                {changes.title && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      fontWeight: "600",
                                                      color: "#374151",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ color: "#6b7280" }}>Title:</span>{" "}
                                                    {changes.title.replace(/^title:\s*/i, "")}
                                                  </div>
                                                )}
                                                {changes.company && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>Company:</span>{" "}
                                                    {changes.company.replace(/^company:\s*/i, "")}
                                                    {changes.location && (
                                                      <span>
                                                        , {changes.location.replace(/^location:\s*/i, "")}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                                {(changes.startDate || changes.endDate) && (
                                                  <div style={{ fontSize: "13px", color: "#4b5563" }}>
                                                    <span style={{ fontWeight: "600" }}>Period:</span>{" "}
                                                    {(changes.startDate || "N/A").replace(/^startDate:\s*/i, "")} -{" "}
                                                    {(changes.endDate || "Present").replace(/^endDate:\s*/i, "")}
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {changes.description && (
                                              <div style={{ marginBottom: "8px" }}>
                                                <div
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    fontWeight: "600",
                                                    marginBottom: "4px",
                                                  }}
                                                >
                                                  Description:
                                                </div>
                                                {(() => {
                                                  const cleanDescription = changes.description.replace(
                                                    /^description:\s*/i,
                                                    ""
                                                  );
                                                  return cleanDescription.includes("•") ? (
                                                    <div
                                                      style={{
                                                        fontSize: "13px",
                                                        color: "#4b5563",
                                                        lineHeight: "1.5",
                                                      }}
                                                    >
                                                      {cleanDescription.split("\n").map((line, idx) => (
                                                        <div key={idx} style={{ marginBottom: "3px" }}>
                                                          {line.trim()}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <div
                                                      style={{
                                                        fontSize: "13px",
                                                        color: "#4b5563",
                                                        lineHeight: "1.5",
                                                      }}
                                                    >
                                                      {cleanDescription}
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            )}

                                            {/* Handle education fields */}
                                            {(changes.degree || changes.school || changes.gpa) && (
                                              <div style={{ marginBottom: "8px" }}>
                                                {changes.degree && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      fontWeight: "600",
                                                      color: "#374151",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ color: "#6b7280" }}>Degree:</span>{" "}
                                                    {changes.degree.replace(/^degree:\s*/i, "")}
                                                  </div>
                                                )}
                                                {changes.school && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>School:</span>{" "}
                                                    {changes.school.replace(/^school:\s*/i, "")}
                                                  </div>
                                                )}
                                                {changes.gpa && (
                                                  <div style={{ fontSize: "13px", color: "#4b5563" }}>
                                                    <span style={{ fontWeight: "600" }}>GPA:</span>{" "}
                                                    {changes.gpa.toString().replace(/^gpa:\s*/i, "")}
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {/* Handle personal information */}
                                            {(changes.name ||
                                              changes.email ||
                                              changes.phone ||
                                              changes.location ||
                                              changes.linkedin ||
                                              changes.github ||
                                              changes.website) && (
                                              <div style={{ marginBottom: "10px" }}>
                                                <div
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    fontWeight: "600",
                                                    marginBottom: "6px",
                                                  }}
                                                >
                                                  Personal Information:
                                                </div>

                                                {changes.name && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>Name:</span>{" "}
                                                    {changes.name.replace(/^name:\s*/i, "")}
                                                  </div>
                                                )}

                                                {changes.email && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>Email:</span>{" "}
                                                    {changes.email.replace(/^email:\s*/i, "")}
                                                  </div>
                                                )}

                                                {changes.phone && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>Phone:</span>{" "}
                                                    {changes.phone.replace(/^phone:\s*/i, "")}
                                                  </div>
                                                )}

                                                {changes.location && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>Location:</span>{" "}
                                                    {changes.location.replace(/^location:\s*/i, "")}
                                                  </div>
                                                )}

                                                {changes.linkedin && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>LinkedIn:</span>{" "}
                                                    {changes.linkedin.replace(/^linkedin:\s*/i, "")}
                                                  </div>
                                                )}

                                                {changes.github && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>GitHub:</span>{" "}
                                                    {changes.github.replace(/^github:\s*/i, "")}
                                                  </div>
                                                )}

                                                {changes.website && (
                                                  <div
                                                    style={{
                                                      fontSize: "13px",
                                                      color: "#4b5563",
                                                      marginBottom: "4px",
                                                    }}
                                                  >
                                                    <span style={{ fontWeight: "600" }}>Website:</span>{" "}
                                                    {changes.website.replace(/^website:\s*/i, "")}
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {/* Handle projects */}
                                            {(changes.name || changes.technologies) &&
                                              !(
                                                changes.email ||
                                                changes.phone ||
                                                changes.location ||
                                                changes.linkedin ||
                                                changes.github ||
                                                changes.website
                                              ) && (
                                                <div style={{ marginBottom: "8px" }}>
                                                  {changes.name && (
                                                    <div
                                                      style={{
                                                        fontSize: "13px",
                                                        fontWeight: "600",
                                                        color: "#374151",
                                                        marginBottom: "4px",
                                                      }}
                                                    >
                                                      <span style={{ color: "#6b7280" }}>Project:</span>{" "}
                                                      {changes.name.replace(/^name:\s*/i, "")}
                                                    </div>
                                                  )}
                                                  {changes.technologies && (
                                                    <div style={{ fontSize: "13px", color: "#4b5563" }}>
                                                      <span style={{ fontWeight: "600" }}>Technologies:</span>{" "}
                                                      {Array.isArray(changes.technologies)
                                                        ? changes.technologies.join(", ")
                                                        : changes.technologies.replace(/^technologies:\s*/i, "")}
                                                    </div>
                                                  )}
                                                </div>
                                              )}

                                            {/* Handle skills */}
                                            {(changes.skills ||
                                              (message.section === "skills" && message.data)) && (
                                              <div style={{ marginBottom: "8px" }}>
                                                <div
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    fontWeight: "600",
                                                    marginBottom: "4px",
                                                  }}
                                                >
                                                  {message.action === "add" ? "Adding New Skills:" : "Skills:"}
                                                </div>
                                                <div
                                                  style={{ fontSize: "13px", color: "#4b5563", lineHeight: "1.5" }}
                                                >
                                                  {(() => {
                                                    // For add-skills commands, use message.data
                                                    const skillsData = changes.skills || message.data;
                                                    if (Array.isArray(skillsData)) {
                                                      return skillsData.join(", ");
                                                    } else if (typeof skillsData === "string") {
                                                      return skillsData;
                                                    }
                                                    return "Skills data";
                                                  })()}
                                                </div>
                                              </div>
                                            )}

                                            {/* Handle certifications */}
                                            {(changes.certifications ||
                                              changes.certification ||
                                              (message.section === "certifications" && message.data)) && (
                                              <div style={{ marginBottom: "8px" }}>
                                                <div
                                                  style={{
                                                    fontSize: "14px",
                                                    color: "#374151",
                                                    fontWeight: "600",
                                                    marginBottom: "4px",
                                                  }}
                                                >
                                                  {message.action === "add"
                                                    ? "Adding New Certification:"
                                                    : "Certification:"}
                                                </div>
                                                <div
                                                  style={{ fontSize: "13px", color: "#4b5563", lineHeight: "1.5" }}
                                                >
                                                  {(() => {
                                                    // Check for different possible sources of certification data
                                                    const certData =
                                                      changes.certifications ||
                                                      changes.certification ||
                                                      message.data;

                                                    // Also check proposedChanges for certification field
                                                    if (
                                                      message.proposedChanges &&
                                                      message.proposedChanges.length > 0
                                                    ) {
                                                      const certChange = message.proposedChanges.find(
                                                        (change) =>
                                                          change.field === "certification" ||
                                                          change.field === "certifications"
                                                      );
                                                      if (certChange && certChange.after) {
                                                        return certChange.after;
                                                      }
                                                    }

                                                    if (certData) {
                                                      if (
                                                        typeof certData === "object" &&
                                                        !Array.isArray(certData)
                                                      ) {
                                                        // Single certification object
                                                        const parts = [];
                                                        if (certData.name) parts.push(`${certData.name}`);
                                                        if (certData.issuer) parts.push(`by ${certData.issuer}`);
                                                        if (certData.date) parts.push(`(${certData.date})`);
                                                        return parts.join(" ");
                                                      } else if (Array.isArray(certData)) {
                                                        // Array of certifications
                                                        return certData
                                                          .map((cert) => {
                                                            const parts = [];
                                                            if (cert.name) parts.push(`${cert.name}`);
                                                            if (cert.issuer) parts.push(`by ${cert.issuer}`);
                                                            if (cert.date) parts.push(`(${cert.date})`);
                                                            return parts.join(" ");
                                                          })
                                                          .join(", ");
                                                      } else if (typeof certData === "string") {
                                                        return certData;
                                                      }
                                                    }
                                                    return "Certification data";
                                                  })()}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#6b7280",
                                          textAlign: "center",
                                          padding: "8px",
                                        }}
                                      >
                                        Preview not available
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            : null}
                        </div>
                      </div>

                      {/* Action Buttons - Only show if no action taken yet */}
                      {message.pendingChanges &&
                        !message.changesAccepted &&
                        !message.changesRejected &&
                        !message.actionId && (
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => handleRejectChanges(message.pendingChanges)}
                              style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                backgroundColor: "transparent",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                color: "#6b7280",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                            >
                              Keep Original
                            </button>
                            <button
                              onClick={() => handleAcceptChanges(message.pendingChanges, message.proposedChanges)}
                              style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                backgroundColor: "#374151",
                                border: "1px solid #374151",
                                borderRadius: "6px",
                                color: "white",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                            >
                              Apply Changes
                            </button>
                          </div>
                        )}

                      {/* Show status after action taken */}
                      {(message.changesAccepted || message.changesRejected) && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "6px 12px",
                            backgroundColor: message.changesAccepted ? "#f0f9ff" : "#f9fafb",
                            border: `1px solid ${message.changesAccepted ? "#3b82f6" : "#d1d5db"}`,
                            borderRadius: "6px",
                            fontSize: "11px",
                            color: message.changesAccepted ? "#1e40af" : "#6b7280",
                            textAlign: "center",
                          }}
                        >
                          {message.changesAccepted
                            ? "✅ Changes Applied Successfully"
                            : "📝 Kept Original Content"}
                        </div>
                      )}
                    </div>
                  )}

                  {message.suggestions && (
                    <div style={{ marginTop: "12px" }}>
                      {message.suggestions
                        .filter((suggestion) => suggestion && suggestion.trim().length > 0)
                        .map((suggestion, index) => (
                          <div
                            key={index}
                            style={{
                              fontSize: "14px",
                              color: "#6b7280",
                              marginBottom: "6px",
                              lineHeight: "1.5",
                              padding: "8px 12px",
                              backgroundColor: "#f3f4f6",
                              borderRadius: "6px",
                              borderLeft: "3px solid #3b82f6",
                            }}
                          >
                            {suggestion}
                          </div>
                        ))}
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#6b7280",
                          marginTop: "8px",
                          marginBottom: "6px",
                        }}
                      >
                        {/* What would you like to enhance first? */}
                      </div>
                      {/* Clear Chat History Button - Only show in first message */}
                      {message.id === 1 && (
                        <Button
                          type="text"
                          size="small"
                          onClick={clearChatHistory}
                          style={{
                            fontSize: "10px",
                            height: "20px",
                            padding: "0 6px",
                          }}
                        >
                          🗑️ Clear Chat History
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Help Content Section - Compact */}
                  {message.helpContent && (
                    <div style={{ marginTop: "8px" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#374151",
                          marginBottom: "8px",
                        }}
                      >
                        {message.helpContent.title}
                      </div>

                      {message.helpContent.sections.map((section, sectionIndex) => (
                        <div key={sectionIndex} style={{ marginBottom: "8px" }}>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#4b5563",
                              marginBottom: "4px",
                            }}
                          >
                            {section.title}
                          </div>

                          {section.content && (
                            <div style={{ marginBottom: "6px" }}>
                              {section.content.map((item, itemIndex) => (
                                <div
                                  key={itemIndex}
                                  style={{
                                    fontSize: "11px",
                                    lineHeight: "1.4",
                                    marginBottom: "2px",
                                    color: "#374151",
                                  }}
                                >
                                  {item}
                                </div>
                              ))}
                            </div>
                          )}

                          {section.note && (
                            <div
                              style={{
                                fontSize: "10px",
                                fontStyle: "italic",
                                color: "#6b7280",
                                marginBottom: "4px",
                              }}
                            >
                              {section.note}
                            </div>
                          )}

                          {section.examples && (
                            <div style={{ marginTop: "4px", marginBottom: "6px" }}>
                              <div
                                style={{
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  color: "#6b7280",
                                  marginBottom: "3px",
                                }}
                              >
                                💡 Examples:
                              </div>
                              {section.examples.map((example, exampleIndex) => (
                                <div
                                  key={exampleIndex}
                                  style={{
                                    backgroundColor: "#f8fafc",
                                    padding: "6px",
                                    borderRadius: "4px",
                                    border: "1px solid #e2e8f0",
                                    marginBottom: "4px",
                                  }}
                                >
                                  <div style={{ marginBottom: "2px" }}>
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        fontFamily: "monospace",
                                        color: "#0366d6",
                                        backgroundColor: "#f0f6ff",
                                        padding: "1px 3px",
                                        borderRadius: "3px",
                                      }}
                                    >
                                      {example.command}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "10px",
                                      fontStyle: "italic",
                                      marginBottom: "2px",
                                    }}
                                  >
                                    Input: "{example.input}"
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "9px",
                                      color: "#6b7280",
                                    }}
                                  >
                                    → {example.description}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {sectionIndex < message.helpContent.sections.length - 1 && (
                            <div style={{ borderTop: "1px solid #e5e7eb", margin: "6px 0" }}></div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Delete Confirmation - Inline Buttons */}
                  {message.isDeleteConfirmation && message.pendingDeleteAction && (
                    <div style={{ marginTop: "12px" }}>
                      <div
                        style={{
                          backgroundColor: "#fff2f0",
                          border: "1px solid #ffccc7",
                          borderRadius: "8px",
                          padding: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "50%",
                              backgroundColor: "#ff4d4f",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "10px",
                            }}
                          >
                            ⚠
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#cf1322",
                            }}
                          >
                            Deleting: {message.pendingDeleteAction.itemLabel}
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize: "12px",
                            color: "#8c8c8c",
                            marginBottom: "12px",
                          }}
                        >
                          This action cannot be undone.
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            onClick={() => handleCancelDelete(message.id)}
                            style={{
                              padding: "4px 12px",
                              fontSize: "12px",
                              backgroundColor: "transparent",
                              border: "1px solid #d9d9d9",
                              borderRadius: "4px",
                              color: "#595959",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                              e.target.style.borderColor = "#40a9ff";
                              e.target.style.color = "#40a9ff";
                            }}
                            onMouseOut={(e) => {
                              e.target.style.borderColor = "#d9d9d9";
                              e.target.style.color = "#595959";
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleConfirmDelete(message.pendingDeleteAction, message.id)}
                            style={{
                              padding: "4px 12px",
                              fontSize: "12px",
                              backgroundColor: "#ff4d4f",
                              border: "1px solid #ff4d4f",
                              borderRadius: "4px",
                              color: "white",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = "#ff7875";
                              e.target.style.borderColor = "#ff7875";
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = "#ff4d4f";
                              e.target.style.borderColor = "#ff4d4f";
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error handling - Compact */}
                  {message.changesMade && message.changesMade.length > 0 && !message.proposedChanges && (
                    <div style={{ marginTop: "8px" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        <span style={{ color: "#374151" }}>Changes made:</span>
                      </div>
                      {message.changesMade.map((change, index) => (
                        <div
                          key={index}
                          style={{
                            fontSize: "11px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          • {change}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    display: "inline-block",
                    backgroundColor: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    color: "#475569",
                    maxWidth: "85%",
                    wordBreak: "break-word",
                  }}
                >
                  {message.content}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Show loading indicator when chat is loading */}
        {isChatLoading && <ChatLoadingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Field with Integrated Command Selection */}
      <div
        style={{
          padding: "8px 12px 12px",
          borderTop: "1px solid #e1e3e5",
          backgroundColor: "#fafbfc",
          flexShrink: 0,
          zIndex: 10,
        }}
      >
        {/* Command Selection - Inside Input Area */}
        <div style={{ marginBottom: "8px" }}>
          {/* Primary Commands */}
          <div style={{ display: "flex", gap: "6px", marginBottom: "6px", flexWrap: "wrap" }}>
            <div
              onClick={() => {
                setSelectedCommand("@add");
                setSelectedSubCommand(null);
                setSelectedField(null);
                setSelectedDeleteItems([]);
              }}
              style={{
                padding: "4px 10px",
                borderRadius: "16px",
                backgroundColor: selectedCommand === "@add" ? "#3b82f6" : "#f1f5f9",
                color: selectedCommand === "@add" ? "#ffffff" : "#64748b",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              ➕ Add
            </div>
            <div
              onClick={() => {
                setSelectedCommand("@enhance");
                setSelectedSubCommand(null);
                setSelectedField(null);
                setSelectedDeleteItems([]);
              }}
              style={{
                padding: "4px 10px",
                borderRadius: "16px",
                backgroundColor: selectedCommand === "@enhance" ? "#3b82f6" : "#f1f5f9",
                color: selectedCommand === "@enhance" ? "#ffffff" : "#64748b",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              ✨ Enhance or Update
            </div>
            <div
              onClick={() => {
                setSelectedCommand("@delete");
                setSelectedSubCommand(null);
                setSelectedField(null);
                setSelectedDeleteItems([]);
              }}
              style={{
                padding: "4px 10px",
                borderRadius: "16px",
                backgroundColor: selectedCommand === "@delete" ? "#dc2626" : "#f1f5f9",
                color: selectedCommand === "@delete" ? "#ffffff" : "#64748b",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              🗑️ Delete
            </div>
            {selectedCommand && (
              <div
                onClick={resetCommand}
                style={{
                  padding: "4px 8px",
                  borderRadius: "16px",
                  backgroundColor: "#fee2e2",
                  color: "#dc2626",
                  fontSize: "12px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                ✕
              </div>
            )}
          </div>

          {/* Secondary Options */}
          {selectedCommand && (
            <div style={{ marginBottom: "6px" }}>
              <div style={{ display: "flex", gap: "4px", marginBottom: "4px", flexWrap: "wrap" }}>
                {Object.keys(commandStructure[selectedCommand].subCommands).map((subCmd) => (
                  <div
                    key={subCmd}
                    onClick={() => {
                      if (!commandStructure[selectedCommand].subCommands[subCmd].disabled) {
                        setSelectedSubCommand(subCmd);
                        setSelectedField(null);
                        setSelectedDeleteItems([]);
                      }
                    }}
                    style={{
                      padding: "3px 8px",
                      borderRadius: "12px",
                      backgroundColor: selectedSubCommand === subCmd ? "#374151" : "#f8fafc",
                      color:
                        selectedSubCommand === subCmd
                          ? "#ffffff"
                          : commandStructure[selectedCommand].subCommands[subCmd].disabled
                          ? "#cbd5e1"
                          : "#475569",
                      fontSize: "11px",
                      fontWeight: "500",
                      cursor: commandStructure[selectedCommand].subCommands[subCmd].disabled
                        ? "not-allowed"
                        : "pointer",
                      transition: "all 0.2s",
                      border: "1px solid " + (selectedSubCommand === subCmd ? "#374151" : "#e2e8f0"),
                      opacity: commandStructure[selectedCommand].subCommands[subCmd].disabled ? 0.5 : 1,
                    }}
                  >
                    {commandStructure[selectedCommand].subCommands[subCmd].label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Specific Field Selection */}
          {selectedCommand &&
            selectedSubCommand &&
            commandStructure[selectedCommand]?.subCommands[selectedSubCommand]?.fields &&
            Array.isArray(commandStructure[selectedCommand].subCommands[selectedSubCommand].fields) &&
            commandStructure[selectedCommand].subCommands[selectedSubCommand].fields.length > 0 && (
              <div style={{ marginBottom: "6px" }}>
                <Text style={{ fontSize: "10px", color: "#64748b", marginBottom: "4px", display: "block" }}>
                  {selectedCommand === "@delete"
                    ? "Select items to delete (multiple selection):"
                    : "Choose specific item:"}
                </Text>
                {selectedCommand === "@delete" ? (
                  // Multi-select checkboxes for delete command
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      padding: "8px",
                      backgroundColor: "#fafbfc",
                    }}
                  >
                    {selectedSubCommand === "skills" ? (
                      // 3-column layout for skills
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: "4px",
                        }}
                      >
                        {commandStructure[selectedCommand].subCommands[selectedSubCommand].fields.map(
                          (field, index) => {
                            const isSelected = selectedDeleteItems.includes(index);
                            const isHeaderItem = field.label.startsWith("[Entire Entry]");
                            const isSubItem = !isHeaderItem; // All non-header items are sub-items (individual fields)

                            return (
                              <div
                                key={index}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedDeleteItems(selectedDeleteItems.filter((i) => i !== index));
                                  } else {
                                    setSelectedDeleteItems([...selectedDeleteItems, index]);
                                  }
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "4px 6px",
                                  marginBottom: "2px",
                                  borderRadius: "4px",
                                  backgroundColor: isSelected ? "#e0f2fe" : "transparent",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  fontWeight: isHeaderItem ? "600" : "normal",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: isHeaderItem ? "#1f2937" : "#4b5563",
                                    flex: 1,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {field.label}
                                </span>
                              </div>
                            );
                          }
                        )}
                      </div>
                    ) : (
                      // Single column layout for other sections
                      <div>
                        {commandStructure[selectedCommand].subCommands[selectedSubCommand].fields.map(
                          (field, index) => {
                            const isSelected = selectedDeleteItems.includes(index);
                            const isHeaderItem = field.label.startsWith("[Entire Entry]");
                            const isSubItem = !isHeaderItem; // All non-header items are sub-items (individual fields)

                            return (
                              <div
                                key={index}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedDeleteItems(selectedDeleteItems.filter((i) => i !== index));
                                  } else {
                                    setSelectedDeleteItems([...selectedDeleteItems, index]);
                                  }
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "4px 6px",
                                  marginBottom: "2px",
                                  borderRadius: "4px",
                                  backgroundColor: isSelected ? "#e0f2fe" : "transparent",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  marginLeft: isSubItem ? "20px" : "0",
                                  fontWeight: isHeaderItem ? "600" : "normal",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = "#f1f5f9";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: isHeaderItem ? "#1f2937" : "#4b5563",
                                    flex: 1,
                                  }}
                                >
                                  {field.label}
                                </span>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  // Single select for other commands
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {commandStructure[selectedCommand].subCommands[selectedSubCommand].fields.map(
                      (field, index) => (
                        <div
                          key={index}
                          onClick={() => setSelectedField(index)}
                          style={{
                            padding: "2px 6px",
                            borderRadius: "10px",
                            backgroundColor: selectedField === index ? "#6b7280" : "#f1f5f9",
                            color: selectedField === index ? "#ffffff" : "#374151",
                            fontSize: "10px",
                            fontWeight: "500",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            border: "1px solid " + (selectedField === index ? "#6b7280" : "#d1d5db"),
                          }}
                        >
                          {field.label}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}

          {/* Selection Summary */}
          {selectedCommand && selectedSubCommand && (
            <div
              style={{
                padding: "4px 8px",
                backgroundColor: "#f8fafc",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                marginBottom: "8px",
              }}
            >
              <Text style={{ fontSize: "10px", color: "#475569" }}>
                <span style={{ fontWeight: "700", color: "#374151" }}>{selectedCommand}</span>
                <span style={{ margin: "0 4px", color: "#cbd5e1" }}>→</span>
                <span style={{ fontWeight: "600", color: "#374151" }}>
                  {commandStructure[selectedCommand].subCommands[selectedSubCommand].label}
                </span>
                {selectedCommand === "@delete" && selectedDeleteItems.length > 0 ? (
                  <>
                    <span style={{ margin: "0 4px", color: "#cbd5e1" }}>→</span>
                    <span style={{ fontWeight: "600", color: "#dc2626" }}>
                      {selectedDeleteItems.length} item{selectedDeleteItems.length !== 1 ? "s" : ""} selected
                    </span>
                  </>
                ) : (
                  selectedField !== null &&
                  commandStructure[selectedCommand].subCommands[selectedSubCommand].fields && (
                    <>
                      <span style={{ margin: "0 4px", color: "#cbd5e1" }}>→</span>
                      <span style={{ fontWeight: "600", color: "#374151" }}>
                        {
                          commandStructure[selectedCommand].subCommands[selectedSubCommand].fields[selectedField]
                            ?.label
                        }
                      </span>
                    </>
                  )
                )}
              </Text>
            </div>
          )}
        </div>

        <div style={{ width: "100%", position: "relative" }}>
          <TextArea
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (isFormValid()) {
                  handleSend();
                }
              }
            }}
            placeholder={
              !selectedCommand || !selectedSubCommand
                ? "Please select a command first or type '/help' for instructions..."
                : selectedCommand === "@delete"
                ? "Press Enter to delete the selected item..."
                : "Describe what you want to do..."
            }
            rows={2}
            autoComplete="off"
            disabled={false}
            style={{
              width: "100%",
              paddingRight: "40px",
              fontSize: "14px",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "8px",
              bottom: "8px",
              cursor: isFormValid() ? "pointer" : "not-allowed",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "24px",
              minHeight: "24px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              border: "1px solid #d9d9d9",
              zIndex: 10,
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isFormValid()) {
                handleSend();
              }
            }}
          >
            <SendOutlined
              style={{
                fontSize: 16,
                color: isFormValid() ? "#1890ff" : "#d9d9d9",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Chat Interface Component
const ChatInterface = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams();
  const fetch = useAuthenticatedFetch();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resumeData, setResumeData] = useState(null);
  const [error, setError] = useState("");
  const [summaryAnalysis, setSummaryAnalysis] = useState(null);

  // Admin AI Testing Modal state
  const [aiPromptModalVisible, setAiPromptModalVisible] = useState(false);
  const [selectedSectionForAI, setSelectedSectionForAI] = useState(null);

  // Check if admin mode is enabled via URL parameter
  const isAdminMode = new URLSearchParams(window.location.search).get("admin") === "true";

  useEffect(() => {
    if (resumeId) {
      fetchResumeData();
    }
  }, [resumeId]);

  const fetchResumeData = async () => {
    try {
      const response = await fetch.get(`/resume/${resumeId}`);
      if (!response.success) {
        throw new Error("Failed to fetch resume data");
      }
      setResumeData(response.data);

      // Load summary analysis from database if it exists
      if (response.data?.parsedData?.summaryAnalysis) {
        setSummaryAnalysis(response.data.parsedData.summaryAnalysis);
      } else {
        setSummaryAnalysis(null);
      }
    } catch (err) {
      // Auth errors (401/403) are handled by Axios interceptor with redirects
      // Only set error for other types of errors
      const errorCode = err?.response?.data?.code || err?.response?.status;
      if (errorCode !== 401 && errorCode !== 403) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendCommand = async (commandString, inputValue, currentResumeData) => {
    try {
      // Handle apply-changes command
      if (commandString === "apply-changes") {
        const response = await fetch.post(
          "/resume/apply-changes",
          {
            resumeId: resumeId,
            changes: inputValue.changes || inputValue,
            proposedChanges: inputValue.proposedChanges || null,
          },
          false
        ); // Disable global loading

        if (response.success) {
          await fetchResumeData();
          return response.data;
        }
        throw new Error(response.message || "Failed to apply changes");
      }

      // Handle delete command
      if (commandString.startsWith("@delete")) {
        const [action, subCommand, fieldIndex] = commandString.split("-");

        // For personalInfo, fieldIndex should be the field name (string), not parsed as integer
        // For other sections, keep the full fieldIndex string to support field-specific deletion (e.g., "0_location")
        let processedFieldIndex;
        if (subCommand === "personalInfo") {
          processedFieldIndex = fieldIndex; // Keep as string for field names like 'phone', 'email'
        } else {
          // Keep the full fieldIndex string to support both entire entry deletion (e.g., "0")
          // and specific field deletion (e.g., "0_location")
          processedFieldIndex = fieldIndex;
        }

        const response = await fetch.post(
          "/resume/delete-item",
          {
            resumeId: resumeId,
            section: subCommand,
            itemIndex: processedFieldIndex,
          },
          false
        ); // Disable global loading

        if (response.success) {
          await fetchResumeData(); // Refresh data after deletion
          return {
            message: response.data.message || `Successfully deleted item from ${subCommand}`,
            deletedSection: response.data.deletedSection,
            deletedIndex: response.data.deletedIndex,
          };
        } else {
          throw new Error(response.message || "Failed to delete item");
        }
      }

      // Send command to OpenAI API through backend
      const response = await fetch.post(
        "/resume/process-command",
        {
          resumeId: resumeId,
          command: commandString,
          content: inputValue,
          resumeData: currentResumeData,
        },
        false
      ); // Disable global loading

      if (response.success) {
        // Store summary analysis data if it's a summary command
        if (response.data.summaryAnalysis) {
          setSummaryAnalysis(response.data.summaryAnalysis);
        } else {
        }
        return response.data;
      } else {
        throw new Error(response.message || "Failed to process command");
      }
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request was cancelled");
      }
      throw error;
    }
  };

  // Handle AI Test button click
  const handleAITest = (sectionName) => {
    if (!resumeData) return;

    // Create resume data structure compatible with AIPromptTestingModal
    const resumeForAI = [
      {
        resumeId: resumeId,
        userName: resumeData.parsedData?.personalInfo?.name || "Unknown",
        userEmail: resumeData.parsedData?.personalInfo?.email || "Unknown",
        parsedData: resumeData.parsedData,
      },
    ];

    setSelectedSectionForAI(resumeForAI);
    setAiPromptModalVisible(true);
  };

  const handlePreview = () => {
    // Check if user is authenticated - if not, redirect to login
    const authToken = localStorage.getItem("authorization");
    const adminToken = sessionStorage.getItem("adminResumeAccessToken");
    if (!authToken && !adminToken && !isAuthenticated) {
      // Redirect to login, after which user will go to resume preview page
      const returnUrl = encodeURIComponent(`/app/resume/${resumeId}/preview`);
      navigate(`/app/login?returnUrl=${returnUrl}`);
      return;
    }

    // User is authenticated - navigate to resume preview
    navigate(`/app/resume/${resumeId}/preview`);
  };

  // Remove initial loading state - show interface immediately
  // if (loading) {
  //   return (
  //     <Layout>
  //       <Content style={{ padding: "24px" }}>
  //         <div style={{ marginBottom: "24px" }}>
  //           <Title level={2}>Resume Chat Assistant</Title>
  //         </div>
  //         <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
  //           <Spin size="large" tip="Loading resume data..." />
  //         </div>
  //       </Content>
  //     </Layout>
  //   );
  // }

  if (error || !resumeData) {
    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          <div style={{ marginBottom: "24px" }}>
            <Title level={2}>Resume Chat Assistant</Title>
          </div>
          <Card>
            <Empty
              description="The resume could not be loaded. Please try uploading a new one."
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            />
          </Card>
        </Content>
      </Layout>
    );
  }

  return (
    <AppLayout>
      <style>{`
        /* CSS Reset to prevent page stretching and maintain sticky positioning */
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          height: 100% !important;
          width: 100% !important;
          overflow-x: clip !important;
          overflow-y: auto !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }

        *, *::before, *::after {
          box-sizing: border-box !important;
        }

        .ant-layout, .ant-layout-content {
          margin: 0 !important;
          padding: 0 !important;
          position: relative !important;
        }

        .ant-layout {
          overflow: visible !important;
        }

        body {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
      `}</style>
      {/* Navigation Header */}

      {/* <div style={{ 
        background: "#ffffff", 
        borderBottom: "1px solid #e5e7eb",
        padding: "12px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
            🤖 AI Resume Chat
          </Title>
          <Badge status="processing" text="Model-based editing" />
        </div>
        
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'choice',
                  label: 'Editing Options',
                  icon: <DownOutlined />,
                  onClick: () => navigate(`/app/resume/${resumeId}/choice`)
                },
                {
                  key: 'modal',
                  label: 'Modal Editor',
                  icon: <PlusOutlined />,
                  onClick: () => navigate(`/app/resume/${resumeId}/modal-based`)
                },
                {
                  key: 'preview',
                  label: 'Preview & Export',
                  icon: <ExclamationCircleOutlined />,
                  onClick: () => navigate(`/app/resume/${resumeId}/preview`)
                }
              ]
            }}
            trigger={['click']}
          >
            <Button icon={<DownOutlined />}>
              Switch Editor
            </Button>
          </Dropdown>
          
          <Button 
            type="primary"
            icon={<ExclamationCircleOutlined />}
            onClick={() => navigate(`/app/resume/${resumeId}/preview`)}
          >
            Preview & Export
          </Button>
        </div>
      </div> */}

      {/* Main Content Area */}
      <div
        style={{
          display: "flex",
          height: "calc(100vh - 73px)", // Subtract header height
          width: "100%",
        }}
      >
        {/* Chat Interface - Left Side - Equal Width */}
        <div
          style={{
            width: "50%",
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid #e5e7eb",
            backgroundColor: "#fafbfc",
          }}
        >
          <ChatPanel resumeData={resumeData} onSendCommand={handleSendCommand} onPreview={handlePreview} />
        </div>

        {/* Resume Preview Panel - Right Side - Equal Width */}
        <div
          style={{
            width: "50%",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <ResumePreviewPanel
            resumeData={resumeData}
            onPreview={handlePreview}
            summaryAnalysis={summaryAnalysis}
            isAdminMode={isAdminMode}
            handleAITest={handleAITest}
          />
        </div>
      </div>

      {/* AI Prompt Testing Modal - Only available in admin mode */}
      {isAdminMode && (
        <AIPromptTestingModal
          visible={aiPromptModalVisible}
          onCancel={() => {
            setAiPromptModalVisible(false);
            setSelectedSectionForAI(null);
          }}
          resumeData={selectedSectionForAI || []}
        />
      )}
    </AppLayout>
  );
};

export default ChatInterface;
